/**
 * PSP/CRE Tool v2.0 - Class VIII Medical Supply Constants
 * 
 * Purpose: Define medical supply categories, consumption rates, MDOS calculations,
 * resupply planning factors, and push package configurations for military medical logistics.
 * 
 * Author: Dr. Tatar Radu MD
 * Date: January 28, 2026
 * Version: 3.3 - ENH-008: DRONE_DELIVERY_SPECS, DRONE_DEFAULT_TYPE, DRONE_DELIVERY_METHOD (March 15, 2026)
 *   CARGO_DRONE: 10 kg payload, 12 PRBC units/flight, 30 km range, cold-chain viable.
 *   LIGHT_DROP_DRONE: 3 kg payload, TCCC consumables only, no blood products.
 *   Both: AD threat availability modifiers (less severe than manned MEDEVAC).
 *   Source: Ukraine 2022-2024 / Project Convergence 22 / Lammers 2023 Transfusion.
 *
 * Version: 3.2 - ENH-021: PUSH_PACKAGES labels shortened + short_summary field added to all 6 entries (March 14, 2026)
 *   Labels: "Emergency Blood Package"→"Emergency Blood", "Standard Resupply Package"→"Standard Resupply",
 *   "MASCAL Push Package"→"MASCAL Emergency", "Surgical Resupply Package"→"Surgical Resupply",
 *   "TCCC Bag Replenishment"→"TCCC Replenishment". "Role 1 Daily Push" label unchanged (already concise).
 *   short_summary: concise one-line content summary for each package (blood counts, weight, cas supported).
 *   Consumed by Tab4_Logistics.jsx v8.12.0 two-line MenuItem. Zero behaviour changes — data only.
 *
 * Version: 3.1 - BUG-106: getPackagesForDestination() deduplicates getter aliases (March 13, 2026)
 *   PUSH_PACKAGES uses JS getter methods (MASS_CASUALTY, SURGICAL_SUPPORT) as aliases.
 *   Object.entries() enumerates them as own properties, producing duplicate MenuItems in
 *   Tab4 dropdown. Fix: Set tracks seen object references; aliases (same ref as canonical)
 *   are skipped. Canonical key always wins (defined first in object literal order).
 *   Zero behaviour change for non-alias keys. No changes to package data or exports.
 *
 * Version: 3.0 - ACT1-SURGICAL-KITS: Remove surgical_kits from app (March 9, 2026)
 *   surgical_kits is a US Army ATP 4-02.25 planning factor only — not a NATO doctrine item.
 *   Removed from: ITEM_WEIGHTS_KG, ITEM_VOLUMES_M3, DEFAULT_ROLE_2_STOCK.
 *   Commented out with "REMOVED FROM APP" note in all three locations — retained for reference.
 *   SURGICAL_RESUPPLY package description updated: clarifies this resupplies consumables/blood,
 *   NOT FST team capacity. surgical_kits removed from package note.
 *
 * CHANGELOG v2.9 (March 6, 2026) - ENH-014b:
 * - ADD: BLOOD_TRANSPORT_LIMITS — maximum transport time (hours) before each blood
 *   product degrades in transit. 7 products: PRBC/FFP/FDP/PLT/CRYO/FWB/LTOWB.
 *   Sources: NATO AMedP-7.1 §3.2–3.5; FRESH_WHOLE_BLOOD warm ischaemia 8h limit.
 *   Consumed by: Tab4 Section 4F cold chain viability matrix (ENH-014b).
 *   FDP: max_hours null — no cold chain, no transport time limit (room temperature).
 *   PLT: max_hours 4 — most restrictive; agitation ceases during road transport.
 *   FWB/LTOWB: max_hours 8 — warm ischaemia limit; WBB collection at R2B, not depot.
 *
 * Version: 2.8 - ARCH-002: MSR forward supply route constants (March 6, 2026)
 *
 * CHANGELOG v2.8 (March 6, 2026) - ARCH-002:
 * - ADD: MSR_ROAD_SPEEDS — base convoy speed (km/h) for 4 road classes (PAVED_HIGHWAY,
 *   PAVED_SECONDARY, UNPAVED_IMPROVED, UNPAVED_UNIMPROVED). Sources: NATO Logistics Handbook,
 *   STANAG 2077, ATP 4-01.45, Romanian ORO-3 road march norms, Ukraine 2022-2024 data.
 * - ADD: MSR_TERRAIN_MODIFIERS — speed multiplier for FLAT / ROLLING / MOUNTAINOUS terrain.
 * - ADD: MSR_SEASON_MODIFIERS — condition multiplier for DRY / WET / MUD (rasputitsa) / SNOW.
 * - ADD: MSR_DEFAULTS — fallback config for first-load (50km, PAVED_SECONDARY, ROLLING, DRY).
 *   Consumed by: supplyChainModule.calculateConvoyTravelTime(), Tab4 Section 4F inputs.
 *
 * CHANGELOG v2.7 (March 6, 2026) - ENH-010:
 * - ADD: ITEM_WEIGHTS_KG — weight (kg) per unit of each tracked supply item.
 *   Keys match calculateDailyConsumption() output keys in supplyChainModule.js.
 *   Sources: NATO MedLog planning factors, ATP 4-02.1, STANAG 2034, Ukraine logistics data.
 *   Consumed by: supplyChainModule.calculateDailyWeightVolume(), Tab4 Convoy Planning card, Tab5 timeline.
 * - ADD: ITEM_VOLUMES_M3 — storage/transport volume (m³) per unit of each tracked supply item.
 *   Includes packaging/stacking allowance. Consumed by same callers as ITEM_WEIGHTS_KG.
 * - ADD: CONVOY_CAPACITY — payload and volume specs for standard convoy vehicles.
 *   Used for truck-count estimation in Tab4 and Tab5 displays (ENH-010 / prerequisite ARCH-002).
 *   STANDARD_TRUCK: NATO medium logistics truck class (IVECO M250 / DAC 665T), 10,000 kg, 30 m³.
 *   LIGHT_TRUCK: Forward distribution vehicle (HMMWV class), 1,100 kg, 2 m³.
 *
 * CHANGELOG v2.6 (March 5, 2026) - PKG-004:
 * - ADD: eligible_destinations string[] field on every PUSH_PACKAGES entry.
 *   Values mirror Tab4 mtf_target: 'ROLE1' | 'ROLE2_BASIC' | 'ROLE2_ENHANCED' | 'ROLE3' | 'ALL'.
 *   ROLE_1_DAILY: ['ROLE1'] only — no blood products, no surgical, unsuitable above battalion.
 *   TCCC_REPLENISHMENT: ['ROLE1', 'ROLE2_BASIC', 'ALL'] — IFAK restock, no cold chain required.
 *   All blood-containing packages: ['ROLE2_BASIC', 'ROLE2_ENHANCED', 'ROLE3', 'ALL'].
 *   Alias getters (MASS_CASUALTY, SURGICAL_SUPPORT) inherit from their source entries.
 * - ADD: getPackagesForDestination(mtfTarget) — filters PUSH_PACKAGES by eligible_destinations.
 *   mtfTarget === 'ROLE1' → only packages with 'ROLE1' in eligible_destinations.
 *   All other targets → only packages that include non-ROLE1 destinations.
 *   Facility-ID targets (specific MTF) treated as Role 2B+ (non-ROLE1 branch).
 *   Consumed by Tab4 package Select dropdown for per-event filtering (PKG-004).
 * - No calculation logic changed.
 *
 * CHANGELOG v2.5 (March 5, 2026) - DOCTRINAL-001/002/003:
 * - ADD: BLOOD_PRODUCT_ECHELON_POLICY — new export constant documenting that blood products
 *   (PRBC, FFP, platelets, whole blood) are available at Role 2B MINIMUM. Role 1 has no
 *   cold chain capability and receives zero blood products. Reference: AJMedP-1 Section 4;
 *   ATP 4-02.2. Addresses DOCTRINAL-001 (force-wide pool lacks echelon annotation).
 * - FIX: CONSUMPTION_RATES.T1_IMMEDIATE.blood_products — added echelon_minimum: 'ROLE_2_BASIC'
 *   and doctrinal_note field. T2_DELAYED.blood_products — same. T3_MINIMAL, T4_EXPECTANT,
 *   DNBI blood_products — added echelon_note confirming no blood products regardless of
 *   echelon. Addresses DOCTRINAL-002 (T1 PRBC rate shown without echelon context).
 * - FIX: MDOS_STANDARDS.BY_ECHELON.ROLE_1 — added blood_products_available: false and
 *   doctrinal_note clarifying no cold chain at battalion level. Notes field updated.
 *   Addresses DOCTRINAL-003 (BY_ECHELON.ROLE_1 implied blood product availability).
 * - No calculation logic changed. All changes are constants annotation only.
 *
 * CHANGELOG v2.4 (February 22, 2026) - EXT-01:
 * - ADD: DOS_THRESHOLDS — module-level alert thresholds for supply chain status displays.
 *   Distinct from MDOS_STANDARDS.STATUS (planning standards): these are simpler go/no-go
 *   thresholds used by supplyChainModule.js and validationSystem.js for status indicators.
 *   Values: OPTIMAL=15, ADEQUATE=7, WARNING=5, LOW=3, CRITICAL=1 (days).
 * - ADD: REQUEST_LEAD_TIMES — administrative pipeline time from request to approval (days).
 *   Distinct from RESUPPLY_FACTORS.PLANNING.resupply_lead_time_days (transport time).
 *   Used by supplyChainModule.js calculateResupplyTiming().
 *   Values: ROUTINE=3, PRIORITY=1, EMERGENCY=0.5 days.
 * - ADD: WASTAGE_FACTORS — per-item-category wastage/spoilage planning rates.
 *   Used by supplyChainModule.js calculateWastageFactor() to replace its local block + `return 0.10`.
 *   Values: BLOOD=0.15, PHARMACEUTICALS=0.05, IV_FLUIDS=0.05, CONSUMABLES=0.10, DEFAULT=0.10.
 * - ADD: BED_NEED_FACTORS — constants for bed requirement estimation in validationSystem.js.
 *   Extracted from calculateEstimatedBedNeed() inline multipliers (0.3, 0.6, 2.5, 1.3).
 *   Fields: DNBI_BED_PERCENT, BATTLE_BED_PERCENT, AVG_LOS_DAYS_DEFAULT,
 *           SURGE_CAPACITY_MULTIPLIER, EVACUATION_RATE_T1_T2.
 * - ADD: MTF_BASE_CONSUMPTION_RATES — baseline Class VIII consumption per role per day,
 *   independent of casualties. Used by supplyIntelligence.js for standing consumption.
 *   Values based on ATP 4-02.1 and Romania Army Medical Logistics Doctrine.
 * - Removed: @institution Romanian Military Medical Institute (APP_RULES 5.3).
 * 
 * CHANGELOG v2.3 (January 28, 2026):
 * - PHASE 4.1: Resupply calculator integration support
 *   - Added EMERGENCY_BLOOD package (20/20/10/5 blood units)
 *   - Added STANDARD_RESUPPLY package (50/40/20/10 blood units)
 *   - Added MASS_CASUALTY alias → MASS_CASUALTY_PUSH
 *   - Added SURGICAL_SUPPORT alias → SURGICAL_RESUPPLY
 * - FIX: TCCC_REPLENISHMENT structure standardized
 *   - Changed from contents_per_ifak to standard contents structure
 *   - Added blood_products (all 0) and pharmaceuticals (all 0)
 *   - Consumables scaled to 50 IFAKs (tourniquets: 100, etc)
 *   - Now compatible with Tab4 display logic
 * - ARCHITECTURE: Single source of truth for push packages
 *   - All 8 packages use consistent nested structure
 *   - contents.blood_products.prbc_units
 *   - contents.pharmaceuticals.iv_fluids_liters
 *   - contents.consumables.tourniquets
 * 
 * CHANGELOG v2.2 (January 11, 2025):
 * - ADD: DEFAULT_CASUALTY_MIX constant (t1: 0.15, t2: 0.25, t3: 0.30, dnbi: 0.30)
 * - FIX: All functions now use DEFAULT_CASUALTY_MIX instead of hardcoded values
 * - FIX: calculateMDOS uses ?? operator for proper null/undefined handling
 * - FIX: getResupplyRequirements uses DEFAULT_CASUALTY_MIX
 * - FIX: calculateSupplyWeight uses DEFAULT_CASUALTY_MIX
 * 
 * CHANGELOG v2.1 (January 11, 2025):
 * - FIX T4-002: calculateMDOS now returns 0 when all stock = 0 (was returning 999)
 * - FIX: Condition checks use !== undefined (was truthy check that skipped 0 values)
 * - ADD: CRITICAL_ZERO status for MDOS = 0
 * 
 * References:
 * - NATO STANAG 2034 (Class VIII Supply Management)
 * - AJMedP-5 Allied Joint Medical Force Health Protection
 * - FM 4-02 Army Health System
 * - ATP 4-02.1 Army Medical Logistics
 * - Romanian Army Medical Logistics Doctrine
 * - Ukraine 2022-2024 Medical Logistics Reports
 * - TCCC Materiel Consumption Studies
 * 
 * Medical Days of Supply (MDOS) Standards:
 * - Optimal: 15-20 DOS
 * - Adequate: 10-15 DOS
 * - Low: 5-10 DOS
 * - Critical: <5 DOS
 * 
 * Ukraine Key Findings:
 * - Tourniquet consumption 3x pre-war estimates
 * - Blood product usage 2x NATO planning factors
 * - TXA now standard - consumption much higher than planned
 * - Hemostatic dressings critical shortage item
 * 
 * Usage:
 * import { 
 *   CLASS_VIII_CATEGORIES,
 *   CONSUMPTION_RATES,
 *   MDOS_STANDARDS,
 *   calculateMDOS,
 *   calculateDailyConsumption,
 *   getResupplyRequirements 
 * } from '../constants/classVIII';
 */

// ============================================================================
// CLASS VIII SUPPLY CATEGORIES
// NATO Standard Medical Supply Classification - Enhanced
// ============================================================================

export const CLASS_VIII_CATEGORIES = {
  A: {
    value: 'A',
    label: 'Pharmaceuticals',
    shortLabel: 'PHARMA',
    description: 'Medications, drugs, biologicals, and official medicinal preparations',
    icon: '💊',
    color: 'primary',
    priority: 1,
    
    subcategories: {
      A1: {
        label: 'Controlled Substances',
        description: 'Narcotics, opioids, controlled medications',
        security_required: true,
        cold_chain: false,
        
        items: {
          morphine: { 
            unit: 'syrette_10mg', 
            shelf_life_months: 24,
            consumption_per_t1: 2,
            consumption_per_t2: 1
          },
          fentanyl: { 
            unit: 'lozenge_400mcg', 
            shelf_life_months: 36,
            consumption_per_t1: 1,
            consumption_per_t2: 0.5
          },
          ketamine: { 
            unit: 'vial_500mg', 
            shelf_life_months: 24,
            consumption_per_t1: 2,
            consumption_per_t2: 1,
            notes: 'Ukraine: Primary battlefield analgesic'
          },
          midazolam: {
            unit: 'vial_5mg',
            shelf_life_months: 24,
            consumption_per_t1: 1,
            consumption_per_surgical: 2
          }
        },
        
        critical_threshold_days: 3,
        notes: 'Chain of custody required. Pre-position at Role 1.'
      },
      
      A2: {
        label: 'Antibiotics',
        description: 'Anti-infective agents for trauma and infection',
        security_required: false,
        cold_chain: false,
        
        items: {
          ceftriaxone: {
            unit: 'vial_1g',
            shelf_life_months: 36,
            consumption_per_surgical: 4,
            consumption_per_infection: 14
          },
          moxifloxacin: {
            unit: 'tab_400mg',
            shelf_life_months: 36,
            consumption_per_penetrating_eye: 14,
            notes: 'Combat eye injury prophylaxis'
          },
          ertapenem: {
            unit: 'vial_1g',
            shelf_life_months: 24,
            consumption_per_t1: 3,
            notes: 'Battlefield antibiotic - single daily dose'
          },
          metronidazole: {
            unit: 'vial_500mg',
            shelf_life_months: 24,
            consumption_per_abdominal: 6
          }
        },
        
        critical_threshold_days: 5,
        notes: 'Early antibiotics reduce infection in penetrating trauma.'
      },
      
      A3: {
        label: 'IV Fluids',
        description: 'Intravenous solutions and electrolytes',
        security_required: false,
        cold_chain: false,
        
        items: {
          normal_saline: {
            unit: 'bag_1000ml',
            weight_kg: 1.1,
            shelf_life_months: 24,
            consumption_per_t1: 4,
            consumption_per_t2: 2,
            consumption_per_t3: 0.5
          },
          lactated_ringers: {
            unit: 'bag_1000ml',
            weight_kg: 1.1,
            shelf_life_months: 24,
            consumption_per_t1: 4,
            consumption_per_t2: 2,
            notes: 'Preferred for trauma resuscitation'
          },
          plasmalyte: {
            unit: 'bag_1000ml',
            weight_kg: 1.1,
            shelf_life_months: 24,
            consumption_per_t1: 2,
            notes: 'Balanced crystalloid - reduced acidosis'
          },
          dextrose_5: {
            unit: 'bag_1000ml',
            weight_kg: 1.1,
            shelf_life_months: 24,
            consumption_per_dnbi: 1
          }
        },
        
        critical_threshold_days: 3,
        weight_factor: 'HIGH',
        notes: 'High weight/volume. Critical for resuscitation.'
      },
      
      A4: {
        label: 'Anesthetics',
        description: 'General and local anesthetics',
        security_required: true,
        cold_chain: false,
        
        items: {
          propofol: {
            unit: 'vial_200mg',
            shelf_life_months: 24,
            consumption_per_surgical: 4
          },
          etomidate: {
            unit: 'vial_20mg',
            shelf_life_months: 24,
            consumption_per_intubation: 1
          },
          lidocaine: {
            unit: 'vial_20ml_2pct',
            shelf_life_months: 36,
            consumption_per_wound: 2
          },
          bupivacaine: {
            unit: 'vial_20ml_0.5pct',
            shelf_life_months: 24,
            consumption_per_nerve_block: 2
          }
        },
        
        critical_threshold_days: 3,
        notes: 'Required for surgical capability.'
      },
      
      A5: {
        label: 'Emergency/Resuscitation Medications',
        description: 'Life-saving emergency drugs',
        security_required: false,
        cold_chain: 'PARTIAL',
        
        items: {
          epinephrine: {
            unit: 'vial_1mg',
            shelf_life_months: 18,
            cold_chain: false,
            consumption_per_cardiac_arrest: 4,
            consumption_per_anaphylaxis: 1
          },
          txa: {
            unit: 'vial_1g',
            shelf_life_months: 36,
            consumption_per_t1: 2,
            consumption_per_t2: 1,
            consumption_per_hemorrhage: 2,
            notes: 'Ukraine: Standard for ALL trauma - much higher consumption than planned'
          },
          calcium_chloride: {
            unit: 'vial_1g',
            shelf_life_months: 24,
            consumption_per_mtp: 4,
            notes: 'Counter citrate toxicity in massive transfusion'
          },
          sodium_bicarbonate: {
            unit: 'vial_50meq',
            shelf_life_months: 24,
            consumption_per_cardiac_arrest: 2
          },
          atropine: {
            unit: 'vial_1mg',
            shelf_life_months: 24,
            consumption_per_nerve_agent: 10,
            consumption_per_bradycardia: 2
          },
          naloxone: {
            unit: 'vial_0.4mg',
            shelf_life_months: 24,
            consumption_per_overdose: 2
          }
        },
        
        critical_threshold_days: 2,
        notes: 'Life-saving. Pre-position forward.'
      },
      
      A6: {
        label: 'TCCC-Specific Medications',
        description: 'Tactical Combat Casualty Care standard medications',
        security_required: false,
        cold_chain: false,
        
        // Ukraine-validated TCCC medication set
        items: {
          combat_pill_pack: {
            contents: ['Moxifloxacin 400mg', 'Acetaminophen 650mg x2'],
            unit: 'pack',
            consumption_per_penetrating: 1,
            notes: 'Self-aid antibiotic for penetrating wound'
          },
          meloxicam: {
            unit: 'tab_15mg',
            consumption_per_casualty: 2,
            notes: 'NSAID - battlefield pain management'
          },
          ondansetron: {
            unit: 'odt_4mg',
            consumption_per_casualty: 2,
            notes: 'Anti-nausea - given with opioids'
          }
        },
        
        critical_threshold_days: 3
      }
    },
    
    storage: {
      temperature_controlled_percent: 15,
      refrigerated_percent: 5,
      frozen_percent: 0,
      controlled_substance_percent: 10,
      average_shelf_life_months: 24
    },
    
    logistics: {
      weight_per_t1_casualty_kg: 3.0,
      weight_per_t2_casualty_kg: 1.5,
      weight_per_t3_casualty_kg: 0.5,
      resupply_priority: 1
    },
    
    notes: 'Critical category. Controlled substances require chain of custody.',
    educational_note: 'TXA consumption in Ukraine was 3x pre-war planning estimates.'
  },
  
  B: {
    value: 'B',
    label: 'Blood Products',
    shortLabel: 'BLOOD',
    description: 'Whole blood, blood components, and derivatives',
    icon: '🩸',
    color: 'error',
    priority: 1,

    // CALC-014 (2026-10-09): moved here from literals in supplyIntelligence (values unchanged; sources still
    // to be confirmed). Stock levels (units) that raise a warning / critical supply alert.
    thresholds: { warning: 30, critical: 15 },

    // Note: Detailed blood product data in bloodProducts.js
    // This is summary for Class VIII planning
    
    subcategories: {
      B1: {
        label: 'Packed Red Blood Cells (PRBC)',
        storage_temp_c: [1, 6],
        shelf_life_days: 42,
        unit_volume_ml: 300,
        critical_threshold_days: 3,
        
        consumption: {
          per_t1_hemorrhage: 6,
          per_t2_hemorrhage: 2,
          per_mtp: 10
        }
      },
      
      B2: {
        label: 'Fresh Frozen Plasma (FFP)',
        storage_temp_c: [-18, -30],
        shelf_life_days: 365,
        thaw_time_minutes: 30,
        unit_volume_ml: 250,
        critical_threshold_days: 3,
        
        consumption: {
          per_mtp: 10,
          per_coagulopathy: 4
        }
      },
      
      B3: {
        label: 'Platelets',
        storage_temp_c: [20, 24],
        shelf_life_days: 5,
        requires_agitation: true,
        unit_volume_ml: 50,
        critical_threshold_days: 2,
        
        consumption: {
          per_mtp: 2,
          per_thrombocytopenia: 1
        },
        
        notes: 'Short shelf life - difficult forward positioning'
      },
      
      B4: {
        label: 'Whole Blood',
        storage_temp_c: [1, 6],
        shelf_life_days: 21,
        unit_volume_ml: 500,
        
        notes: 'Walking Blood Bank collection. Gold standard for trauma.',
        ukraine_finding: 'Whole blood preferred in forward surgery'
      },
      
      B5: {
        label: 'Freeze-Dried Plasma',
        storage_temp_c: 'ROOM',
        shelf_life_months: 24,
        reconstitution_minutes: 3,
        
        notes: 'No cold chain required. Ideal for forward positioning.',
        availability: 'Limited - French/German production'
      }
    },
    
    storage: {
      requires_blood_bank: true,
      temperature_controlled_percent: 100,
      refrigerated_percent: 40,
      frozen_percent: 55,
      room_temp_percent: 5
    },
    
    logistics: {
      weight_per_t1_casualty_kg: 5.0,
      cold_chain_mandatory: true,
      resupply_priority: 1
    },
    
    notes: 'Life-critical. Cold chain mandatory. See bloodProducts.js for details.',
    educational_note: 'Ukraine showed 2x blood consumption vs NATO planning factors.'
  },
  
  C: {
    value: 'C',
    label: 'Medical Equipment',
    shortLabel: 'EQUIP',
    description: 'Durable medical equipment and devices',
    icon: '🔬',
    color: 'info',
    priority: 2,
    
    subcategories: {
      C1: {
        label: 'Monitoring Equipment',
        items: {
          vital_signs_monitor: { per_role2: 8, maintenance_parts: true },
          pulse_oximeter: { per_role1: 2, per_role2: 10 },
          capnograph: { per_role2: 4 },
          defibrillator: { per_role1: 1, per_role2: 2 }
        }
      },
      
      C2: {
        label: 'Ventilators',
        items: {
          transport_vent: { per_medevac: 1, per_role2: 4 },
          icu_vent: { per_role2e: 4, per_role3: 20 }
        },
        
        notes: 'Critical for T1 casualties and surgical cases'
      },
      
      C3: {
        label: 'Imaging',
        items: {
          portable_xray: { per_role2: 1 },
          ultrasound: { per_role2: 2 },
          ct_scanner: { per_role3: 1 }
        }
      },
      
      C4: {
        label: 'Surgical Equipment',
        items: {
          surgical_table: { per_or: 1 },
          surgical_lights: { per_or: 2 },
          anesthesia_machine: { per_or: 1 },
          suction: { per_or: 2, per_role1: 1 }
        }
      }
    },
    
    logistics: {
      resupply_priority: 2,
      maintenance_intensive: true
    },
    
    notes: 'Durable equipment. Maintenance and parts critical.'
  },
  
  D: {
    value: 'D',
    label: 'Medical Consumables',
    shortLabel: 'CONSUMABLES',
    description: 'Disposable medical supplies and dressings',
    icon: '🩹',
    color: 'secondary',
    priority: 1,
    
    subcategories: {
      D1: {
        label: 'Hemorrhage Control',
        description: 'TCCC hemorrhage control items',
        
        items: {
          tourniquet_cat: {
            unit: 'each',
            consumption_per_extremity_wound: 1.5,  // Often multiple applied
            shelf_life_months: 60,
            notes: 'Ukraine: 3x consumption vs planning'
          },
          tourniquet_soft_t: {
            unit: 'each',
            consumption_per_extremity_wound: 0.5
          },
          combat_gauze: {
            unit: 'roll',
            consumption_per_hemorrhage: 2,
            shelf_life_months: 36,
            notes: 'QuikClot/Celox impregnated'
          },
          hemostatic_dressing: {
            unit: 'each',
            consumption_per_wound: 1,
            notes: 'Critical shortage in Ukraine'
          },
          pressure_dressing_israeli: {
            unit: 'each',
            consumption_per_wound: 1,
            shelf_life_months: 60
          },
          chest_seal: {
            unit: 'each',
            consumption_per_chest_wound: 2,  // Entry + exit
            shelf_life_months: 60
          },
          junctional_tourniquet: {
            unit: 'each',
            consumption_per_junctional: 1,
            notes: 'Groin/axilla hemorrhage - high-value item'
          }
        },
        
        critical_threshold_days: 3
      },
      
      D2: {
        label: 'Airway Management',
        
        items: {
          npa: {
            unit: 'each',
            sizes: ['24F', '26F', '28F', '30F'],
            consumption_per_airway_casualty: 1
          },
          king_lt: {
            unit: 'each',
            sizes: [3, 4, 5],
            consumption_per_failed_airway: 1
          },
          cric_kit: {
            unit: 'kit',
            consumption_per_surgical_airway: 1,
            notes: 'Surgical cricothyrotomy'
          },
          et_tube: {
            unit: 'each',
            sizes: ['6.0', '6.5', '7.0', '7.5', '8.0'],
            consumption_per_intubation: 1.5  // Allow for failed attempts
          },
          bvm: {
            unit: 'each',
            consumption_per_resuscitation: 1
          }
        }
      },
      
      D3: {
        label: 'IV Access',
        
        items: {
          iv_catheter: {
            unit: 'each',
            sizes: ['14G', '16G', '18G', '20G'],
            consumption_per_casualty: 3,  // Multiple attempts
            shelf_life_months: 36
          },
          io_needle: {
            unit: 'each',
            types: ['EZ-IO', 'FAST1'],
            consumption_per_io: 1,
            notes: 'Intraosseous - combat preferred'
          },
          iv_tubing: {
            unit: 'set',
            consumption_per_iv: 1
          },
          extension_set: {
            unit: 'each',
            consumption_per_iv: 1
          },
          pressure_bag: {
            unit: 'each',
            consumption_per_rapid_infusion: 1
          },
          saline_lock: {
            unit: 'each',
            consumption_per_iv: 1
          }
        }
      },
      
      D4: {
        label: 'Wound Care',
        
        items: {
          gauze_4x4: {
            unit: 'pack_200',
            consumption_per_wound: 20
          },
          kerlix: {
            unit: 'roll',
            consumption_per_wound: 2
          },
          ace_wrap: {
            unit: 'roll',
            consumption_per_extremity: 1
          },
          abdominal_pad: {
            unit: 'each',
            consumption_per_abdominal: 4
          },
          wound_vac: {
            unit: 'kit',
            consumption_per_complex_wound: 1,
            notes: 'Role 2+ only'
          }
        }
      },
      
      D5: {
        label: 'Hypothermia Prevention',
        
        items: {
          hypothermia_blanket: {
            unit: 'each',
            types: ['HPMK', 'Ready-Heat'],
            consumption_per_casualty: 1,
            notes: 'Ukraine: Critical - cold casualties common'
          },
          blood_warmer: {
            unit: 'set',
            consumption_per_mtp: 1
          },
          warm_iv_fluid_set: {
            unit: 'each',
            consumption_per_hypothermia: 1
          }
        }
      }
    },
    
    logistics: {
      weight_per_t1_casualty_kg: 5.0,
      weight_per_t2_casualty_kg: 2.0,
      weight_per_t3_casualty_kg: 0.5,
      resupply_priority: 1
    },
    
    notes: 'Highest consumption category. Pre-position heavily forward.',
    educational_note: 'Tourniquet and hemostatic consumption in Ukraine far exceeded planning.'
  },
  
  E: {
    value: 'E',
    label: 'Surgical Supplies',
    shortLabel: 'SURGICAL',
    description: 'Surgical instruments, sutures, and OR supplies',
    icon: '🔪',
    color: 'warning',
    priority: 2,
    
    subcategories: {
      E1: {
        label: 'Surgical Instruments',
        
        sets: {
          major_surgery_set: { per_or: 2, instruments: 80 },
          minor_surgery_set: { per_role1: 1, per_or: 4, instruments: 30 },
          orthopedic_set: { per_or: 1, instruments: 60 },
          vascular_set: { per_or: 1, instruments: 40 },
          thoracotomy_set: { per_or: 1, instruments: 35 },
          laparotomy_set: { per_or: 2, instruments: 50 }
        },
        
        notes: 'Reusable - requires sterilization capability'
      },
      
      E2: {
        label: 'Sutures',
        
        items: {
          vicryl: {
            sizes: ['0', '2-0', '3-0'],
            consumption_per_surgery: 6
          },
          prolene: {
            sizes: ['3-0', '4-0', '5-0'],
            consumption_per_vascular: 4
          },
          chromic: {
            sizes: ['0', '2-0'],
            consumption_per_surgery: 4
          },
          nylon: {
            sizes: ['3-0', '4-0'],
            consumption_per_closure: 3
          },
          stapler_skin: {
            consumption_per_surgery: 1
          }
        }
      },
      
      E3: {
        label: 'OR Disposables',
        
        items: {
          surgical_drapes: { consumption_per_surgery: 1 },
          surgical_gown: { consumption_per_surgery: 3 },
          surgical_gloves: { consumption_per_surgery: 6 },
          scalpel_blade: { sizes: [10, 15, 20], consumption_per_surgery: 3 },
          cautery_tip: { consumption_per_surgery: 2 }
        }
      },
      
      E4: {
        label: 'Implants/Hardware',
        
        items: {
          external_fixator_set: {
            consumption_per_ortho: 0.3,
            notes: 'High-value, limited availability'
          },
          chest_tube: {
            sizes: ['28F', '32F', '36F'],
            consumption_per_thoracic: 1.5
          },
          foley_catheter: {
            sizes: ['14F', '16F', '18F'],
            consumption_per_surgery: 1
          }
        }
      }
    },
    
    logistics: {
      weight_per_surgery_kg: 3.0,
      resupply_priority: 2
    }
  },
  
  F: {
    value: 'F',
    label: 'Dental Supplies',
    shortLabel: 'DENTAL',
    description: 'Dental instruments and materials',
    icon: '🦷',
    color: 'info',
    priority: 3,
    
    notes: 'Lower priority in combat. Maxillofacial trauma uses surgical supplies.'
  },
  
  G: {
    value: 'G',
    label: 'Radiology Supplies',
    shortLabel: 'RADIOLOGY',
    description: 'X-ray and imaging supplies',
    icon: '📡',
    color: 'info',
    priority: 3,
    
    subcategories: {
      G1: {
        label: 'X-ray',
        items: {
          xray_cassette: { per_role2: 10 },
          contrast_media: { per_ct_scan: 1 }
        }
      }
    }
  },
  
  H: {
    value: 'H',
    label: 'Medical Gases',
    shortLabel: 'GASES',
    description: 'Oxygen, anesthesia gases, and compressed air',
    icon: '💨',
    color: 'info',
    priority: 2,
    
    subcategories: {
      H1: {
        label: 'Oxygen',
        
        items: {
          oxygen_d_cylinder: {
            volume_liters: 350,
            weight_kg: 5,
            duration_at_2lpm_hours: 3,
            consumption_per_t1: 2,
            consumption_per_surgery: 3
          },
          oxygen_e_cylinder: {
            volume_liters: 680,
            weight_kg: 8,
            duration_at_2lpm_hours: 5.5
          },
          oxygen_concentrator: {
            output_lpm: 10,
            power_watts: 350,
            notes: 'Reduce cylinder dependency'
          }
        },
        
        critical_threshold_days: 2,
        notes: 'Heavy - logistics challenge. Concentrators help.'
      },
      
      H2: {
        label: 'Anesthesia Gases',
        items: {
          sevoflurane: { consumption_per_surgery_ml: 100 },
          nitrous_oxide: { cylinder_per_role2e: 2 }
        }
      }
    },
    
    logistics: {
      weight_factor: 'VERY_HIGH',
      special_handling: 'COMPRESSED_GAS'
    }
  },
  
  I: {
    value: 'I',
    label: 'CBRN Medical Supplies',
    shortLabel: 'CBRN',
    description: 'Antidotes, decontamination supplies, and protective equipment',
    icon: '☣️',
    color: 'error',
    priority: 'CONDITIONAL',
    
    // Note: Detailed in cbrnMedical.js
    subcategories: {
      I1: {
        label: 'Nerve Agent Antidotes',
        items: {
          atnaa_kit: { per_soldier: 3, notes: 'Atropine + 2-PAM' },
          cana: { per_soldier: 1, notes: 'Diazepam' },
          atropine_bulk: { per_mass_casualty: 100 },
          pralidoxime_bulk: { per_mass_casualty: 100 }
        }
      },
      
      I2: {
        label: 'Cyanide Antidotes',
        items: {
          cyanokit: { per_role2: 5 }
        }
      },
      
      I3: {
        label: 'Radiation',
        items: {
          potassium_iodide: { per_soldier: 14 },
          prussian_blue: { per_role2: 20 }
        }
      }
    },
    
    notes: 'Stock based on CBRN threat assessment.'
  }
};

// ============================================================================
// CONSUMPTION RATES BY CASUALTY TYPE
// Ukraine-validated consumption rates per casualty
// ============================================================================

export const CONSUMPTION_RATES = {
  T1_IMMEDIATE: {
    label: 'T1 Immediate - Life-threatening',
    description: 'Severe hemorrhage, airway compromise, tension pneumothorax',
    
    pharmaceuticals: {
      iv_fluids_liters: 6.0,
      txa_grams: 2,
      ketamine_mg: 500,
      morphine_doses: 2,
      antibiotics_doses: 3,
      emergency_meds_doses: 4
    },
    
    blood_products: {
      prbc_units: 6,
      ffp_units: 6,
      platelets_units: 1,
      cryo_units: 2,
      whole_blood_units: 4,  // If available - preferred
      // DOCTRINAL-002 (AJMedP-1 Section 4; ATP 4-02.2):
      // Blood products are available at ROLE_2_BASIC minimum.
      // Role 1 (BAS) has NO cold chain — these rates apply from Role 2B upward only.
      // A T1 casualty at Role 1 receives haemostasis and airway management, NOT transfusion.
      echelon_minimum: 'ROLE_2_BASIC',
      doctrinal_note: 'Transfusion begins at Role 2B. Role 1 provides haemostatic resuscitation only (TXA, tourniquets, pressure). No blood cold chain at battalion level.',
      notes: 'Massive transfusion protocol likely'
    },
    
    consumables: {
      tourniquets: 2,
      hemostatic_dressings: 3,
      chest_seals: 2,
      iv_starts: 4,
      io_access: 1,
      dressing_sets: 4,
      hypothermia_blankets: 2
    },
    
    surgical: {
      or_time_hours: 2.5,
      suture_packs: 8,
      surgical_supplies_sets: 1
    },
    
    medical_gases: {
      oxygen_liters: 2000,  // ~6 hours at high flow
      notes: 'Ventilator support common'
    },
    
    weight_kg_total: 25,
    cost_estimate_usd: 15000,
    
    notes: 'Ukraine data: T1 consumption 30% higher than NATO planning factors'
  },
  
  T2_DELAYED: {
    label: 'T2 Delayed - Serious but stable',
    description: 'Non-life-threatening wounds, fractures, burns <20%',
    
    pharmaceuticals: {
      iv_fluids_liters: 3.0,
      txa_grams: 1,
      ketamine_mg: 250,
      morphine_doses: 1,
      antibiotics_doses: 2,
      emergency_meds_doses: 1
    },
    
    blood_products: {
      prbc_units: 2,
      ffp_units: 2,
      platelets_units: 0,
      cryo_units: 0,
      whole_blood_units: 0,
      // DOCTRINAL-002: See T1_IMMEDIATE. Same echelon restriction applies.
      echelon_minimum: 'ROLE_2_BASIC',
      doctrinal_note: 'Transfusion if Hgb <7 or ongoing bleeding — administered at Role 2B+. Role 1 does not hold or transfuse blood products.',
      notes: 'Transfusion if Hgb <7 or ongoing bleeding'
    },
    
    consumables: {
      tourniquets: 0.5,
      hemostatic_dressings: 1,
      chest_seals: 0.5,
      iv_starts: 2,
      io_access: 0,
      dressing_sets: 2,
      hypothermia_blankets: 1
    },
    
    surgical: {
      or_time_hours: 1.5,
      suture_packs: 4,
      surgical_supplies_sets: 0.5
    },
    
    medical_gases: {
      oxygen_liters: 500
    },
    
    weight_kg_total: 12,
    cost_estimate_usd: 5000
  },
  
  T3_MINIMAL: {
    label: 'T3 Minimal - Walking wounded',
    description: 'Minor wounds, simple fractures, minor burns',
    
    pharmaceuticals: {
      iv_fluids_liters: 0.5,
      txa_grams: 0,
      ketamine_mg: 0,
      morphine_doses: 0,
      antibiotics_doses: 1,
      emergency_meds_doses: 0,
      oral_analgesics_doses: 4
    },
    
    blood_products: {
      prbc_units: 0,
      ffp_units: 0,
      // DOCTRINAL-002: Zero blood products regardless of echelon. T3 casualties
      // do not require transfusion under standard triage protocol.
      notes: 'Transfusion rare'
    },
    
    consumables: {
      tourniquets: 0,
      hemostatic_dressings: 0,
      chest_seals: 0,
      iv_starts: 0.5,
      dressing_sets: 1,
      hypothermia_blankets: 0.5
    },
    
    surgical: {
      or_time_hours: 0.5,
      suture_packs: 2,
      surgical_supplies_sets: 0
    },
    
    medical_gases: {
      oxygen_liters: 0
    },
    
    weight_kg_total: 3,
    cost_estimate_usd: 500
  },
  
  T4_EXPECTANT: {
    label: 'T4 Expectant - Comfort care',
    description: 'Non-survivable injuries, comfort measures only',
    
    pharmaceuticals: {
      iv_fluids_liters: 1.0,
      morphine_doses: 4,
      sedatives_doses: 2,
      notes: 'Comfort care focus'
    },
    
    blood_products: {
      prbc_units: 0,
      // DOCTRINAL-002: Zero blood products by triage protocol — resource preservation.
      // T4 expectant doctrine prohibits consumption of scarce blood products regardless of echelon.
      notes: 'No transfusion - resource preservation'
    },
    
    consumables: {
      dressing_sets: 1,
      hypothermia_blankets: 1
    },
    
    weight_kg_total: 2,
    cost_estimate_usd: 200,
    
    notes: 'Ethical: Comfort and dignity. Do not prolong suffering.'
  },
  
  DNBI: {
    label: 'DNBI - Disease and Non-Battle Injury',
    description: 'Illness, accidents, heat/cold injury',
    
    pharmaceuticals: {
      iv_fluids_liters: 1.0,
      antibiotics_doses: 5,
      oral_medications_doses: 10
    },
    
    blood_products: {
      prbc_units: 0,
      // DOCTRINAL-002: Blood products not planned for DNBI. If transfusion exceptionally
      // required (e.g. severe anaemia), applies at Role 2B+ only per standard echelon policy.
      notes: 'Rarely needed'
    },
    
    consumables: {
      iv_starts: 1,
      dressing_sets: 0.5
    },
    
    weight_kg_total: 2,
    cost_estimate_usd: 300
  },
  
  // Special categories
  SURGICAL_CASE: {
    label: 'Per Surgical Case',
    description: 'Additional consumption per surgery',
    
    surgical: {
      suture_packs: 4,
      surgical_drapes: 1,
      surgical_gowns: 3,
      surgical_gloves_pairs: 6,
      scalpel_blades: 3,
      sponges: 20
    },
    
    anesthesia: {
      propofol_vials: 4,
      fentanyl_mcg: 200,
      paralytic_doses: 2,
      sevoflurane_ml: 100
    },
    
    monitoring: {
      ecg_electrodes: 5,
      spo2_sensor: 1
    }
  },
  
  MTP_CASE: {
    label: 'Massive Transfusion Protocol',
    description: 'Per MTP activation',
    
    blood_products: {
      prbc_units: 10,
      ffp_units: 10,
      platelets_units: 2,
      cryo_units: 10,
      calcium_vials: 4
    },
    
    notes: 'MTP = 10+ units in 24 hours or 4+ in first hour'
  }
};

// ============================================================================
// DEFAULT CASUALTY MIX
// Standard triage distribution for medical planning
// Used by calculateMDOS, calculateDailyConsumption, and related functions
// ============================================================================

/**
 * Default casualty distribution by triage category
 * Based on NATO medical planning factors and Ukraine 2022-2024 data
 * 
 * IMPORTANT: All modules (classVIII, nationalLogistics, supplyIntelligence, Tab4)
 * MUST use this constant for consistency in MDOS calculations.
 * 
 * Distribution:
 * - Combat casualties: ~70% (T1 + T2 + T3)
 * - DNBI (Disease/Non-Battle Injury): ~30%
 */
export const DEFAULT_CASUALTY_MIX = {
  t1: 0.15,    // 15% - Immediate (life-threatening)
  t2: 0.25,    // 25% - Delayed (serious but stable)
  t3: 0.30,    // 30% - Minimal (walking wounded)
  dnbi: 0.30,  // 30% - Disease and Non-Battle Injury
};

// ============================================================================
// BLOOD PRODUCT ECHELON POLICY (DOCTRINAL-001)
// ============================================================================

/**
 * Authoritative echelon restriction for blood product availability.
 *
 * DOCTRINAL BASIS: AJMedP-1 Section 4; ATP 4-02.2; STANAG 2228.
 *
 * Blood products (PRBC, FFP, platelets, cryoprecipitate, whole blood) require
 * a continuous cold chain (2–6°C). Role 1 (Battalion Aid Station) has no cold
 * chain infrastructure and therefore stocks and administers ZERO blood products.
 *
 * PLANNING IMPLICATION: The blood consumption values in CONSUMPTION_RATES
 * (prbc_units, ffp_units, etc.) represent clinical requirements that are fulfilled
 * at ROLE_2_BASIC or above. The calculation engine models consumption as a
 * force-wide aggregate pool — this is an acknowledged simplification
 * (see DOCTRINAL-001). Echelon-restricted modeling is a future enhancement.
 *
 * EDUCATIONAL NOTE for PSP/CRE Tool users:
 * A T1 casualty at a Role 1 BAS receives haemostatic resuscitation (TXA, tourniquet,
 * pressure dressing, airway management) and is evacuated forward to Role 2B within
 * 4 hours. Transfusion begins at Role 2B. Any planning model that routes blood to
 * Role 1 is doctrinally incorrect.
 */
export const BLOOD_PRODUCT_ECHELON_POLICY = {
  minimum_echelon: 'ROLE_2_BASIC',
  minimum_echelon_label: 'Role 2 Basic (Brigade Medical Company)',
  blood_products_at_role_1: false,

  rationale: [
    'Role 1 has no cold chain (2–6°C refrigeration) — AJMedP-1 Section 4',
    'Role 1 maximum hold: 4 hours — patient must reach Role 2B for definitive haemostasis',
    'PRBC shelf life requires continuous refrigeration unavailable at battalion level',
    'Walking Blood Bank (WBB) donors are pre-registered at Role 2B+, not Role 1',
    'ATP 4-02.2: blood products are a Role 2B and above capability'
  ],

  products_affected: ['prbc_units', 'ffp_units', 'platelets_units', 'cryo_units', 'whole_blood_units'],

  role_1_capabilities: [
    'TXA (Tranexamic Acid) — haemostatic adjunct, no cold chain required',
    'Tourniquet application and junctional haemorrhage control',
    'Haemostatic dressings (QuikClot, Combat Gauze)',
    'IV access and crystalloid bolus (limited — DCR preferred at Role 2B)',
    'Airway management (NPA, surgical airway)',
    'Needle chest decompression, chest seal'
  ],

  sources: [
    'AJMedP-1 Section 4 — Medical Support to Operations',
    'ATP 4-02.2 — Medical Evacuation',
    'STANAG 2228 — Medical Support Planning',
    'CoTCCC April 2025 — TCCC Guidelines',
    'Ukraine 2022–2024: forward blood programme begins at Role 2B (DSMeD reporting)'
  ],

  // Current model limitation (DOCTRINAL-001):
  // Blood is pooled force-wide without echelon routing. This is a known simplification.
  // Echelon-restricted blood modeling is tracked under DOCTRINAL-001 for future implementation.
  model_limitation: 'DOCTRINAL-001: current engine uses force-wide blood pool. Echelon-restricted routing not yet implemented. Survival curves and DOW rates partially compensate.'
};

// ============================================================================
// MDOS (MEDICAL DAYS OF SUPPLY) STANDARDS
// NATO and Romania-specific supply level standards
// ============================================================================

export const MDOS_STANDARDS = {
  // Overall status thresholds
  STATUS: {
    OPTIMAL: {
      days: 20,
      color: 'success',
      action: 'Maintain current levels'
    },
    ADEQUATE: {
      days: 15,
      color: 'info',
      action: 'Monitor, routine resupply'
    },
    LOW: {
      days: 10,
      color: 'warning',
      action: 'Priority resupply required'
    },
    CRITICAL: {
      days: 5,
      color: 'error',
      action: 'Emergency resupply - operational risk'
    },
    EMERGENCY: {
      days: 3,
      color: 'error',
      action: 'IMMEDIATE resupply - mission failure risk'
    }
  },
  
  // By echelon
  BY_ECHELON: {
    ROLE_1: {
      label: 'Role 1 (Battalion Aid Station)',
      standard_dos: 3,
      minimum_dos: 1,
      maximum_dos: 5,
      resupply_frequency: 'DAILY',
      // DOCTRINAL-003 (AJMedP-1 Section 4; ATP 4-02.2):
      // Role 1 has NO cold chain infrastructure. Blood products (PRBC, FFP, platelets,
      // whole blood) are NOT stocked, stored, or administered at battalion level.
      // MDOS_STANDARDS at Role 1 covers pharmaceuticals, consumables, and dressings ONLY.
      // Blood product MDOS planning begins at ROLE_2_BASIC.
      blood_products_available: false,
      doctrinal_note: 'No blood cold chain at Role 1. DOS entries for pharmaceuticals and consumables only. Blood product planning: see ROLE_2_BASIC and above. A collection point at Role 1 holding blood is a doctrinal violation (AJMedP-1).',
      notes: 'Limited storage. Daily push from Role 2B. No blood products — consumables and pharmaceuticals only.'
    },
    
    ROLE_2_BASIC: {
      label: 'Role 2 Basic',
      standard_dos: 10,
      minimum_dos: 5,
      maximum_dos: 15,
      resupply_frequency: 'EVERY_3_DAYS',
      
      notes: 'Primary resupply point for Role 1s.'
    },
    
    ROLE_2_ENHANCED: {
      label: 'Role 2 Enhanced (FST)',
      standard_dos: 15,
      minimum_dos: 7,
      maximum_dos: 20,
      resupply_frequency: 'WEEKLY',
      
      notes: 'Surgical capability requires deeper stocks.'
    },
    
    ROLE_3: {
      label: 'Role 3 (Combat Support Hospital)',
      standard_dos: 20,
      minimum_dos: 10,
      maximum_dos: 30,
      resupply_frequency: 'WEEKLY',
      
      notes: 'Theater-level stock. Supports multiple Role 2s.'
    }
  },
  
  // By category priority
  BY_CATEGORY: {
    BLOOD_PRODUCTS: {
      minimum_dos: 3,
      critical_dos: 2,
      notes: 'Shortest shelf life, highest priority'
    },
    
    CONTROLLED_SUBSTANCES: {
      minimum_dos: 5,
      critical_dos: 3,
      notes: 'Limited supply chain, pre-position'
    },
    
    IV_FLUIDS: {
      minimum_dos: 5,
      critical_dos: 3,
      notes: 'Heavy, bulk item'
    },
    
    HEMORRHAGE_CONTROL: {
      minimum_dos: 7,
      critical_dos: 5,
      notes: 'Highest consumption item - stock heavily'
    },
    
    SURGICAL_SUPPLIES: {
      minimum_dos: 10,
      critical_dos: 5,
      notes: 'Required for surgical capability'
    },
    
    OXYGEN: {
      minimum_dos: 3,
      critical_dos: 2,
      notes: 'Heavy, special handling'
    }
  }
};

// ============================================================================
// PUSH PACKAGES
// Pre-configured resupply packages
// ============================================================================

export const PUSH_PACKAGES = {
  // ==========================================================================
  // BLOOD PRODUCT FOCUSED PACKAGES
  // ==========================================================================
  
  EMERGENCY_BLOOD: {
    label: 'Emergency Blood',
    // ENH-021: short_summary for Tab4 dropdown tooltip/caption
    short_summary: 'Blood only · 20 PRBC · 20 FFP · 10 PLT · 5 Cryo · ~5 cas · 50 kg',
    description: 'Rapid hemorrhage control - blood products only for critical shortages',
    // PKG-004: Blood cold chain required — Role 2B minimum (AJMedP-1; BLOOD_PRODUCT_ECHELON_POLICY)
    eligible_destinations: ['ROLE2_BASIC', 'ROLE2_ENHANCED', 'ROLE3', 'ALL'],
    
    contents: {
      blood_products: {
        prbc_units: 20,
        ffp_units: 20,
        platelets_units: 10,
        cryo_units: 5
      }
    },
    
    supports_casualties: 5,
    weight_kg: 50,
    volume_cubic_m: 0.2,
    delivery_priority: 'EMERGENCY',
    notes: 'Used for predicted stockouts or MASCAL surge'
  },
  
  STANDARD_RESUPPLY: {
    label: 'Standard Resupply',
    // ENH-021: short_summary for Tab4 dropdown tooltip/caption
    short_summary: 'Balanced · 50 PRBC · 40 FFP · 50 IV · 30 TQ · ~15 cas · 250 kg',
    description: 'Routine operational resupply with balanced blood products and consumables',
    // PKG-004: Contains blood products — Role 2B minimum
    eligible_destinations: ['ROLE2_BASIC', 'ROLE2_ENHANCED', 'ROLE3', 'ALL'],
    
    contents: {
      blood_products: {
        prbc_units: 50,
        ffp_units: 40,
        platelets_units: 20,
        cryo_units: 10
      },
      
      pharmaceuticals: {
        iv_fluids_liters: 50,
        txa_vials: 20,
        antibiotics_doses: 50,
        morphine_doses: 20,
        ketamine_vials: 10
      },
      
      consumables: {
        tourniquets: 30,
        hemostatic_dressings: 30,
        chest_seals: 15,
        iv_start_kits: 50,
        dressing_packs: 30
      }
    },
    
    supports_casualties: 15,
    weight_kg: 250,
    volume_cubic_m: 1.2,
    delivery_priority: 'ROUTINE',
    notes: 'Most common resupply package for sustained operations'
  },
  
  // ==========================================================================
  // ROLE-BASED PACKAGES
  // ==========================================================================
  
  ROLE_1_DAILY: {
    label: 'Role 1 Daily Push',
    // ENH-021: short_summary for Tab4 dropdown tooltip/caption
    short_summary: 'BAS only · no blood · 20 IV · 10 TQ · O₂ · ~5 cas · 100 kg',
    description: 'Standard daily resupply for battalion aid station',
    // PKG-004: NO blood products, NO surgical. Inappropriate above battalion level.
    // Selecting this for Role 2B/2E/3 targets results in zero blood delivery — silent error.
    eligible_destinations: ['ROLE1'],
    
    contents: {
      pharmaceuticals: {
        iv_fluids_liters: 20,
        morphine_doses: 10,
        ketamine_vials: 5,
        txa_vials: 10,
        antibiotics_doses: 20
      },
      
      consumables: {
        tourniquets: 10,
        hemostatic_dressings: 10,
        chest_seals: 6,
        iv_start_kits: 20,
        dressing_packs: 10,
        hypothermia_blankets: 5
      },
      
      medical_gases: {
        oxygen_d_cylinders: 4
      }
    },
    
    supports_casualties: 5,
    weight_kg: 100,
    volume_cubic_m: 0.5
  },
  
  MASS_CASUALTY_PUSH: {
    label: 'MASCAL Emergency',
    // ENH-021: short_summary for Tab4 dropdown tooltip/caption
    short_summary: 'MASCAL surge · 50 PRBC · 50 FFP · 100 IV · 50 TQ · WBB kits · ~30 cas · 500 kg',
    description: 'Emergency resupply for mass casualty event',
    // PKG-004: Contains blood products — Role 2B minimum
    eligible_destinations: ['ROLE2_BASIC', 'ROLE2_ENHANCED', 'ROLE3', 'ALL'],
    
    contents: {
      pharmaceuticals: {
        iv_fluids_liters: 100,
        morphine_doses: 50,
        ketamine_vials: 25,
        txa_vials: 50,
        antibiotics_doses: 100,
        atropine_vials: 20,
        epinephrine_vials: 20
      },
      
      blood_products: {
        prbc_units: 50,
        ffp_units: 50,
        whole_blood_collection_kits: 20
      },
      
      consumables: {
        tourniquets: 50,
        hemostatic_dressings: 50,
        chest_seals: 30,
        iv_start_kits: 100,
        io_kits: 20,
        dressing_packs: 50,
        hypothermia_blankets: 30,
        litters: 20
      }
    },
    
    supports_casualties: 30,
    weight_kg: 500,
    volume_cubic_m: 2.5,
    delivery_priority: 'EMERGENCY'
  },
  
  SURGICAL_RESUPPLY: {
    label: 'Surgical Resupply',
    // ENH-021: short_summary for Tab4 dropdown tooltip/caption
    short_summary: 'OR consumables · 30 PRBC · 30 FFP · sutures/drapes/anaesthesia · R2B+ · ~20 ops · 200 kg',
    // NOTE: This package name is retained for backward compatibility.
    // It does NOT resupply "surgical capacity" (FST hours) — that is a personnel/team
    // issue fixed in Tab 3. This package resupplies surgical consumables and blood products
    // (sutures, anaesthetics, drapes, PRBC/FFP) for Role 2B+ facilities.
    // surgical_kits removed — US Army ATP 4-02.25 only, not NATO doctrine.
    description: 'Surgical consumables and blood products for Role 2B+ facilities (sutures, anaesthetics, PRBC/FFP)',
    // PKG-004: Contains blood products + surgical consumables — requires FST/surgical capability (Role 2B+)
    eligible_destinations: ['ROLE2_BASIC', 'ROLE2_ENHANCED', 'ROLE3', 'ALL'],
    
    contents: {
      surgical: {
        suture_packs_assorted: 100,
        surgical_drapes: 20,
        surgical_gowns: 60,
        surgical_gloves_pairs: 200,
        scalpel_blades: 50,
        chest_tubes: 10,
        foley_catheters: 20,
        external_fixator_sets: 5
      },
      
      anesthesia: {
        propofol_vials: 40,
        etomidate_vials: 20,
        fentanyl_vials: 30,
        paralytic_vials: 20,
        sevoflurane_bottles: 5
      },
      
      blood_products: {
        prbc_units: 30,
        ffp_units: 30,
        platelets_units: 10
      }
    },
    
    supports_surgeries: 20,
    weight_kg: 200,
    volume_cubic_m: 1.0
  },
  
  TCCC_REPLENISHMENT: {
    label: 'TCCC Replenishment',
    // ENH-021: short_summary for Tab4 dropdown tooltip/caption
    short_summary: 'IFAKs only · no blood · 100 TQ · 50 chest seals · 50 bags · 75 kg',
    description: 'Restock individual TCCC/IFAK supplies',
    // PKG-004: No blood products — consumables/IFAK only. Valid at Role 1 and Role 2B.
    // Role 2E/3 would typically use STANDARD_RESUPPLY for individual TCCC top-up.
    eligible_destinations: ['ROLE1', 'ROLE2_BASIC', 'ALL'],
    
    contents: {
      blood_products: {
        prbc_units: 0,
        ffp_units: 0,
        platelets_units: 0,
        cryo_units: 0
      },
      
      pharmaceuticals: {
        iv_fluids_liters: 0,
        antibiotics_doses: 0,
        morphine_doses: 0
      },
      
      consumables: {
        tourniquets: 100,              // 50 IFAKs × 2 per IFAK
        combat_gauze_packs: 50,        // 50 IFAKs × 1 per IFAK
        chest_seals: 50,               // 50 IFAKs × 1 pair per IFAK
        pressure_dressings: 100,       // 50 IFAKs × 2 per IFAK
        npa_airways: 50,               // 50 IFAKs × 1 per IFAK
        ncd_needles: 50,               // 50 IFAKs × 1 per IFAK
        gloves_pairs: 100,             // 50 IFAKs × 2 per IFAK
        shears: 50                     // 50 IFAKs × 1 per IFAK
      }
    },
    
    ifaks_per_package: 50,
    supports_casualties: 50,
    weight_kg: 75,
    volume_cubic_m: 0.3,
    delivery_priority: 'ROUTINE'
  },
  
  // ==========================================================================
  // ALIASES FOR BACKWARD COMPATIBILITY
  // ==========================================================================
  
  // Alias: MASS_CASUALTY → MASS_CASUALTY_PUSH
  get MASS_CASUALTY() {
    return this.MASS_CASUALTY_PUSH;
  },
  
  // Alias: SURGICAL_SUPPORT → SURGICAL_RESUPPLY
  get SURGICAL_SUPPORT() {
    return this.SURGICAL_RESUPPLY;
  }
};

// ============================================================================
// PACKAGE DESTINATION FILTER (PKG-004)
// ============================================================================

/**
 * Returns the subset of PUSH_PACKAGES valid for a given resupply destination.
 *
 * PKG-004 fix: Prevents ROLE_1_DAILY (zero blood products, zero surgical) from
 * appearing in the package selector for Role 2B/2E/3 or ALL-MTF destinations.
 * Consumed by Tab4_Logistics.jsx resupply event package Select dropdown.
 *
 * BUG-106 FIX: PUSH_PACKAGES contains getter aliases (MASS_CASUALTY → MASS_CASUALTY_PUSH,
 * SURGICAL_SUPPORT → SURGICAL_RESUPPLY). Object.entries() enumerates getters as own
 * enumerable properties, so both the canonical key and its alias appear in the result —
 * producing duplicate MenuItems in the Tab4 dropdown. Fix: track seen object references
 * via a Set and skip any entry whose object has already been included.
 *
 * @param {string} mtfTarget - Value from event.mtf_target:
 *   'ROLE1' | 'ROLE2_BASIC' | 'ROLE2_ENHANCED' | 'ROLE3' | 'ALL' | <facility_id>
 *   Facility IDs (specific MTFs from Tab3) are treated as Role 2B+ (non-ROLE1 branch).
 *
 * @returns {Object} - Filtered subset of PUSH_PACKAGES safe for the given destination,
 *   with alias duplicates removed (canonical key always wins — it appears first in
 *   object definition order).
 */
export function getPackagesForDestination(mtfTarget) {
  const isRole1Target = mtfTarget === 'ROLE1';
  const seen = new Set(); // BUG-106 FIX: track object identity to skip alias duplicates
  return Object.fromEntries(
    Object.entries(PUSH_PACKAGES).filter(([, pkg]) => {
      // BUG-106 FIX: skip if this exact object reference was already included
      // (means this entry is a getter alias pointing to an already-seen canonical package)
      if (seen.has(pkg)) return false;
      seen.add(pkg);
      // Aliases (getters) without own eligible_destinations — allow if source allowed.
      // Since we now skip aliases entirely above, this guard handles any future plain
      // entries without the field.
      if (!Array.isArray(pkg.eligible_destinations)) return true;
      if (isRole1Target) {
        return pkg.eligible_destinations.includes('ROLE1');
      }
      // For any non-ROLE1 target (including specific facility IDs): exclude packages
      // whose eligibility is restricted to ROLE1 only.
      return !pkg.eligible_destinations.every(d => d === 'ROLE1');
    })
  );
}

// ============================================================================
// RESUPPLY FACTORS
// Logistics planning factors
// ============================================================================

export const RESUPPLY_FACTORS = {
  // CALC-014 (2026-10-09): moved here from a literal in supplyIntelligence (value unchanged).
  // Recommended interval between routine resupply runs.
  STANDARD: { interval_days: 3 },

  // Transport capacity
  TRANSPORT: {
    AMBULANCE: {
      supply_capacity_kg: 100,
      supply_capacity_m3: 0.5,
      notes: 'Limited - patient transport primary'
    },
    
    LMTV: {
      supply_capacity_kg: 2500,
      supply_capacity_m3: 10,
      volume_capacity_liters: 10000,  // DEAD-003: explicit litres — IV fluids/blood are volume-constrained
      notes: 'Primary Class VIII transport'
    },
    
    HEMTT: {
      supply_capacity_kg: 10000,
      supply_capacity_m3: 25,
      notes: 'Heavy lift'
    },
    
    HELICOPTER: {
      supply_capacity_kg: 1500,
      supply_capacity_m3: 5,
      notes: 'For emergency resupply'
    }
  },
  
  // Planning factors
  PLANNING: {
    weight_per_casualty_kg: 15,
    volume_per_casualty_m3: 0.1,
    
    safety_stock_multiplier: 1.5,   // 50% safety stock
    wastage_factor: 1.1,            // 10% wastage
    
    resupply_lead_time_days: {
      emergency: 1,
      priority: 3,
      routine: 7
    }
  },
  
  // Cold chain
  COLD_CHAIN: {
    blood_products: {
      transport_temp_c: [1, 10],
      max_transport_hours: 24,
      requires_monitoring: true
    },
    
    vaccines: {
      transport_temp_c: [2, 8],
      max_transport_hours: 48,
      requires_monitoring: true
    }
  }
};

// ============================================================================
// BLOOD_TRANSPORT_LIMITS
// ENH-014b: Maximum transport time (hours) before each blood product degrades.
// Used by Tab4 Section 4F cold chain viability matrix to flag MSR legs that
// exceed product viability windows.
//
// Sources:
//   PRBC:  NATO AMedP-7.1 §3.2 — max 24h at 1–6°C during transport
//   FFP:   NATO AMedP-7.1 §3.4 — frozen transport; thaw at destination; 24h
//          practical limit for planning (same as PRBC)
//   FDP:   Freeze-Dried Plasma — no cold chain, no transport time limit (null)
//   PLT:   NATO AMedP-7.1 §3.3 — max 4h (agitation ceases during road transport)
//   CRYO:  NATO AMedP-7.1 §3.5 — frozen transport; 24h limit same as PRBC/FFP
//   FWB:   Fresh Whole Blood — 8h warm ischaemia limit (FRESH_WHOLE_BLOOD constant)
//   LTOWB: Low-Titer O+ Whole Blood — same as FWB (same product, different donor)
//
// FWB and LTOWB are WBB donor-generated at R2B — depot transport not applicable
// in practice, but limit defined for completeness and planning awareness.
// ============================================================================

export const BLOOD_TRANSPORT_LIMITS = {
  PRBC:  { max_hours: 24,   cold_chain: true,  label: 'PRBC',  note: '1–6°C continuous refrigeration' },
  FFP:   { max_hours: 24,   cold_chain: true,  label: 'FFP',   note: 'Frozen transport; thaw at destination' },
  FDP:   { max_hours: null, cold_chain: false, label: 'FDP',   note: 'Room temperature — no transport time limit' },
  PLT:   { max_hours: 4,    cold_chain: false, label: 'PLT',   note: '20–24°C + agitation required; most restrictive' },
  CRYO:  { max_hours: 24,   cold_chain: true,  label: 'CRYO',  note: 'Frozen transport; same limit as PRBC/FFP' },
  FWB:   { max_hours: 8,    cold_chain: true,  label: 'FWB',   note: 'Warm ischaemia 8h max; WBB-generated at R2B' },
  LTOWB: { max_hours: 8,    cold_chain: true,  label: 'LTOWB', note: 'Same as FWB; universal donor (low-titer O+)' },
};

// ============================================================================
// DOS_THRESHOLDS
// EXT-01: Module-level alert thresholds for supply status displays and
// go/no-go decisions. Used by supplyChainModule.js and validationSystem.js.
//
// DISTINCTION from MDOS_STANDARDS.STATUS:
//   MDOS_STANDARDS.STATUS = full planning standard (days, color, action, notes)
//   DOS_THRESHOLDS         = simple numeric threshold for comparison logic
//
// References: ATP 4-02.1 Medical Logistics; Romania Army Medical Logistics Doctrine
// ============================================================================

export const DOS_THRESHOLDS = {
  OPTIMAL:   15,  // >= 15 days: Green — no action
  ADEQUATE:   7,  // >= 7 days:  Blue/Info — monitor
  WARNING:    5,  // >= 5 days:  Yellow — initiate resupply request
  LOW:        3,  // >= 3 days:  Orange — priority resupply
  CRITICAL:   1   // >= 1 day:   Red — emergency resupply, operational risk
};

// ============================================================================
// REQUEST_LEAD_TIMES
// EXT-01: Administrative pipeline time (days) from request submission to
// approval and order release. Does NOT include transport time.
//
// DISTINCTION from RESUPPLY_FACTORS.PLANNING.resupply_lead_time_days:
//   resupply_lead_time_days = transport/delivery time only
//   REQUEST_LEAD_TIMES       = request processing/approval time only
//
// Total resupply time = REQUEST_LEAD_TIMES[priority] + transport_time
// References: ATP 4-02.1; AJP-4.10 Allied Joint Medical Support
// ============================================================================

export const REQUEST_LEAD_TIMES = {
  ROUTINE:   3,    // days — standard logistics channel
  PRIORITY:  1,    // days — command-expedited channel
  EMERGENCY: 0.5   // days (12 hours) — commander's emergency authority
};

// ============================================================================
// WASTAGE_FACTORS
// EXT-01: Planning rates for spoilage, breakage, and administrative loss
// by supply category. Applied on top of consumption estimates.
//
// Used by supplyChainModule.js calculateWastageFactor() — replaces its local
// block and `return 0.10` fallback (SCM-06, Phase 3).
//
// References: STANAG 2034; Ukraine 2022-2024 logistics reports;
//   ATP 4-02.1 Medical Logistics wastage planning factors
// ============================================================================

export const WASTAGE_FACTORS = {
  BLOOD:           0.15,  // 15% — short shelf life, cold chain breaks, type mismatch
  PHARMACEUTICALS: 0.05,  // 5%  — controlled storage, reasonable shelf life
  IV_FLUIDS:       0.05,  // 5%  — breakage, contamination
  CONSUMABLES:     0.10,  // 10% — loss, contamination, expiry in field conditions
  SURGICAL:        0.08,  // 8%  — sterility breach, packaging damage
  DEFAULT:         0.10   // 10% — general fallback for unclassified items
};

// ============================================================================
// BED_NEED_FACTORS
// EXT-01: Constants for bed requirement estimation.
// Extracted from validationSystem.js calculateEstimatedBedNeed() inline
// multipliers — single authoritative source per APP_RULES 2.1.
//
// Formula (validationSystem.js):
//   battle_beds = (strength/1000) × BASE_CASUALTY_RATE × state_mod × posture_mod
//               × BATTLE_BED_PERCENT × AVG_LOS_DAYS_DEFAULT
//   dnbi_beds   = (strength/1000) × dnbi_rate × DNBI_BED_PERCENT × AVG_LOS_DAYS_DEFAULT
//
// References: AJMedP-1 Allied Joint Medical Publication;
//   ATP 4-02.12 Casualty Estimation; Romanian Army Medical Standards
// ============================================================================

export const BED_NEED_FACTORS = {
  // % of DNBI casualties requiring a bed (vs ambulatory treatment)
  DNBI_BED_PERCENT:          0.30,   // 30% — per DNBI category breakdown

  // % of battle casualties requiring a bed (not immediately evacuated or OR)
  BATTLE_BED_PERCENT:        0.60,   // 60% — T2/T3 held, T1 evacuated

  // Default average length of stay when THP not configured (days)
  AVG_LOS_DAYS_DEFAULT:      2.5,    // days — NATO planning factor

  // Average LOS by Theatre Holding Policy configuration
  AVG_LOS_BY_THP: {
    THP_3_DAY:  1.5,
    THP_5_DAY:  2.0,
    THP_7_DAY:  2.5,
    THP_15_DAY: 5.0,
    THP_30_DAY: 10.0
  },

  // % of T1/T2 evacuated to higher echelon within 24h (reduces bed occupancy)
  EVACUATION_RATE_T1_T2:     0.50,   // 50% — NATO planning standard

  // Surge capacity multiplier above normal bed establishment
  SURGE_CAPACITY_MULTIPLIER: 1.30    // 30% above normal — Role 2 surge doctrine
};

// ============================================================================
// MTF_BASE_CONSUMPTION_RATES
// EXT-01: Baseline Class VIII consumption per MTF per day, independent of
// casualties. Accounts for standing operations, staff, and minimum readiness.
//
// Used by supplyIntelligence.js for baseline (non-casualty) consumption.
// All values are per facility per day.
//
// References: ATP 4-02.1 Table B-2; AJMedP-5 Health Protection;
//   Romania Army Medical Logistics Doctrine; Ukraine 2022-2024 field reports
// ============================================================================

export const MTF_BASE_CONSUMPTION_RATES = {
  ROLE_1: {
    label: 'Role 1 — Battalion Aid Station',
    iv_fluids_liters:     10,
    morphine_doses:        2,
    antibiotics_doses:     5,
    txa_vials:             2,
    tourniquets:           2,
    hemostatic_dressings:  2,
    dressing_sets:         5,
    gloves_pairs:         20,
    oxygen_d_cylinders:    0.5,
    notes: 'Minimum readiness stock consumption. No surgical capability.'
  },

  ROLE_2_BASIC: {
    label: 'Role 2 Basic — Brigade Medical Company',
    iv_fluids_liters:     50,
    morphine_doses:        5,
    antibiotics_doses:    15,
    txa_vials:             5,
    ketamine_vials:        3,
    prbc_units:            2,
    ffp_units:             2,
    tourniquets:           5,
    hemostatic_dressings:  5,
    dressing_sets:        15,
    surgical_gloves_pairs:10,
    gloves_pairs:         50,
    oxygen_d_cylinders:    1,
    notes: 'Includes DCS preparation readiness. Blood product minimum turnover.'
  },

  ROLE_2_ENHANCED: {
    label: 'Role 2 Enhanced — FST/Surgical Capability',
    iv_fluids_liters:    100,
    morphine_doses:       10,
    antibiotics_doses:    25,
    txa_vials:            10,
    ketamine_vials:        8,
    propofol_vials:        4,
    prbc_units:            4,
    ffp_units:             4,
    platelets_units:       1,
    tourniquets:          10,
    hemostatic_dressings: 10,
    dressing_sets:        30,
    surgical_gloves_pairs:30,
    surgical_drapes:       5,
    suture_packs:          5,
    oxygen_d_cylinders:    2,
    notes: 'Surgical support stock. OR readiness consumption included.'
  },

  ROLE_3: {
    label: 'Role 3 — Combat Support Hospital',
    iv_fluids_liters:    300,
    morphine_doses:       30,
    antibiotics_doses:    75,
    txa_vials:            20,
    ketamine_vials:       20,
    propofol_vials:       15,
    fentanyl_mcg:        500,
    prbc_units:           10,
    ffp_units:            10,
    platelets_units:       4,
    cryo_units:            2,
    tourniquets:          20,
    hemostatic_dressings: 20,
    dressing_sets:        80,
    surgical_gloves_pairs:80,
    surgical_drapes:      15,
    suture_packs:         20,
    scalpel_blades:       10,
    oxygen_d_cylinders:    6,
    notes: 'Full hospital baseline. Multiple simultaneous ORs. ICU included.'
  }
};

// ============================================================================
// UKRAINE LOGISTICS LESSONS
// Empirical data from 2022-2024
// ============================================================================

export const UKRAINE_LOGISTICS_LESSONS = {
  CONSUMPTION_VARIANCES: {
    tourniquets: {
      planned_per_casualty: 0.5,
      actual_per_casualty: 1.5,
      variance: '+200%',
      reason: 'Liberal application, multiple tourniquets per extremity, training emphasis'
    },
    
    hemostatic_dressings: {
      planned_per_casualty: 0.5,
      actual_per_casualty: 1.5,
      variance: '+200%',
      reason: 'High junctional wounds from artillery, limited tourniquet application sites'
    },
    
    txa: {
      planned_per_casualty: 0.3,
      actual_per_casualty: 0.9,
      variance: '+200%',
      reason: 'Now standard for ALL trauma per TCCC update'
    },
    
    blood_products: {
      planned_per_t1: 4,
      actual_per_t1: 8,
      variance: '+100%',
      reason: 'Whole blood preference, higher hemorrhage volume from blast'
    },
    
    hypothermia_prevention: {
      planned_per_casualty: 0.2,
      actual_per_casualty: 0.8,
      variance: '+300%',
      reason: 'Cold climate, prolonged evacuation times'
    }
  },
  
  CRITICAL_SHORTAGES: [
    {
      item: 'Hemostatic dressings',
      frequency: 'FREQUENT',
      impact: 'Preventable deaths from junctional hemorrhage',
      recommendation: 'Stock 3x planning factor'
    },
    {
      item: 'Tourniquets',
      frequency: 'MODERATE',
      impact: 'Field improvisation required',
      recommendation: 'Stock 2x planning factor'
    },
    {
      item: 'Blood products',
      frequency: 'FREQUENT',
      impact: 'Delayed transfusion, increased mortality',
      recommendation: 'Expand Walking Blood Bank, freeze-dried plasma'
    },
    {
      item: 'Chest seals',
      frequency: 'OCCASIONAL',
      impact: 'Improvisation with available materials',
      recommendation: 'Ensure adequate forward stocks'
    }
  ],
  
  LOGISTICS_INNOVATIONS: [
    {
      innovation: 'Drone medical resupply',
      effectiveness: 'HIGH',
      description: 'Delivery of blood, medications to positions under fire',
      limitations: 'Weather, payload weight, battery life'
    },
    {
      innovation: 'Walking Blood Bank forward deployment',
      effectiveness: 'HIGH',
      description: 'Pre-screened donors in combat units',
      impact: 'Whole blood available at point of wounding'
    },
    {
      innovation: 'Civilian supply chain integration',
      effectiveness: 'MODERATE',
      description: 'Leveraging civilian pharmacy networks',
      limitations: 'Controlled substances, military-specific items'
    }
  ]
};

// ============================================================================
// ENH-010: WEIGHT / VOLUME CONSTANTS FOR CONVOY PLANNING
// ============================================================================
//
// ITEM_WEIGHTS_KG: weight in kg per unit of each item tracked by calculateDailyConsumption().
//   Keys are identical to the keys returned by calculateDailyConsumption() / MATERIEL_ITEMS / BLOOD_ITEMS.
//   Used by calculateDailyWeightVolume() in supplyChainModule.js.
//
// Sources:
//   Blood products — NATO blood logistics planning factors (STANAG 2034, NATO Blood Panel)
//   Crystalloid    — 1L bag: fluid (1 kg) + LDPE bag/tubing/packaging (0.10 kg) = 1.10 kg/L
//   Consumables    — CAT tourniquet manufacturer spec (86 g), CoTCCC field data
//   Surgical kit   — ATP 4-02.25 sterile procedure pack planning weight (~2 kg incl outer wrap)
//   Analgesics     — Morphine syrette: ~10 g unit weight
//   Other items    — NATO medical logistics planning estimates
//
// NOTE: Blood product weights include outer packaging but NOT dedicated cold-box allocation.
//   Cold chain infrastructure (ice packs, coolers) is a separate logistics line item.

export const ITEM_WEIGHTS_KG = {
  // Blood products (per standard unit)
  prbc:                 0.40,  // PRBC unit: ~300 mL packed cells + SAGM + bag + label (~350 mL total)
  ffp:                  0.30,  // FFP unit: 250 mL frozen plasma + cryobag
  platelets:            0.08,  // Platelet concentrate: 50–60 mL unit + agitation bag

  // IV fluids (per liter)
  crystalloid:          1.10,  // 1 L bag (NS/LR/PlasmaLyte): fluid + LDPE container + administration set

  // Medical materiel (per dose / unit as defined in calculateDailyConsumption())
  antibiotics:          0.05,  // IV antibiotic vial (1 g, 20 mL glass) + packaging
  analgesics:           0.01,  // Morphine syrette 10 mg / fentanyl lozenge unit
  tourniquets:          0.09,  // CAT tourniquet (86 g manufacturer spec + packaging)
  chest_seals:          0.05,  // Single seal (Hyfin/HaloSeal): ~30 g + blister pack
  dressings_sets:       0.25,  // Field dressing set: hemostatic dressing + Israeli bandage + gauze
  // REMOVED FROM APP — surgical_kits is a US Army ATP 4-02.25 planning factor only,
  // not a NATO doctrine item. Retained here for reference only. Not used in simulation.
  // surgical_kits: 2.00,
  controlled_substances: 0.05, // Anesthesia vial set per surgical case (ketamine + propofol + midazolam vials)
};

// ITEM_VOLUMES_M3: storage/transport volume in m³ per unit.
//   Includes reasonable packaging and stacking allowance (×1.2 of pure fluid volume).
//   Used alongside ITEM_WEIGHTS_KG for convoy planning calculations.

export const ITEM_VOLUMES_M3 = {
  // Blood products
  prbc:                 0.00050,  // ~0.5 L per unit (bag + outer wrap), stacking allowance
  ffp:                  0.00040,  // ~0.4 L per frozen unit
  platelets:            0.00010,  // ~0.1 L per unit

  // IV fluids (per liter of fluid — bag is approx 1.2× the fluid volume with packaging)
  crystalloid:          0.00120,  // 1 L bag: 1.0 L fluid × 1.2 packaging factor = 1.2 L = 0.0012 m³

  // Materiel
  antibiotics:          0.00008,  // Small IV vial (~20 mL) + secondary packaging
  analgesics:           0.00002,  // Syrette / lozenge unit — minimal volume
  tourniquets:          0.00020,  // CAT tourniquet: ~200 cm³ including pouch
  chest_seals:          0.00010,  // Single blister pack: ~100 cm³
  dressings_sets:       0.00035,  // Set of dressings in ziplock: ~350 cm³
  // REMOVED FROM APP — see ITEM_WEIGHTS_KG note above.
  // surgical_kits: 0.00250,
  controlled_substances: 0.00008, // Vial set: ~80 cm³ (similar to antibiotics vial set)
};

// CONVOY_CAPACITY: vehicle payload and volume for truck-count estimation.
//   Used by Tab4 Convoy Planning card and Tab5 logistics timeline.
//   NATO planning: always limit to 80% of stated capacity (surge/safety reserve).
//
// Sources: STANAG 2034, NATO Logistics Handbook, Romanian Army TAB-3 logistics doctrine

export const CONVOY_CAPACITY = {
  STANDARD_TRUCK: {
    label:              'NATO Logistics Truck (IVECO M250 / DAC 665T class)',
    payload_kg:         10000,   // 10-tonne payload — standard NATO medium logistics truck
    volume_m3:          30.0,    // 30 m³ cargo body (covered / isothermal body option)
    planning_factor:    0.80,    // Use 80% capacity for convoy planning (safety + securing)
    effective_payload_kg: 8000,  // 10,000 × 0.80
    effective_volume_m3:  24.0,  // 30.0 × 0.80
    notes: 'Standard NATO medium logistics truck. Cold-chain variant available (isothermal body) for blood products.',
  },
  LIGHT_TRUCK: {
    label:              'Light Utility Vehicle (HMMWV / ARO 244 class)',
    payload_kg:         1100,
    volume_m3:          2.0,
    planning_factor:    0.80,
    effective_payload_kg: 880,
    effective_volume_m3:  1.6,
    notes: 'Last-mile forward distribution. Not suitable for bulk blood product transport.',
  },
};

// ============================================================================
// MSR / FORWARD SUPPLY ROUTE CONSTANTS — ARCH-002
//
// Speed values for convoy travel time calculation.
// Based on: NATO Logistics Handbook, STANAG 2077, ATP 4-01.45 (Convoy Operations),
//           Romanian Army ORO-3 road march norms, Ukraine 2022-2024 operational data.
//
// Speed here is ground convoy speed for logistics vehicles (not ambulances).
// All speeds in km/h. Apply terrain modifier × season modifier for effective speed.
// ============================================================================

/**
 * Base convoy speeds (km/h) by road type.
 * These are planning speeds for loaded logistics truck convoys — conservative by design.
 * Night movement: multiply by 0.70. Under indirect fire threat: multiply by 0.80.
 */
export const MSR_ROAD_SPEEDS = {
  PAVED_HIGHWAY: {
    label:       'Paved Highway (Class A road)',
    speed_kmh:   60,
    notes:       'Autostrada / main highway. Good surface, wide lanes. Typical NATO GLOC.',
  },
  PAVED_SECONDARY: {
    label:       'Paved Secondary Road (Class B road)',
    speed_kmh:   45,
    notes:       'Two-lane paved. Standard MSR in Central European theater.',
  },
  UNPAVED_IMPROVED: {
    label:       'Unpaved Improved Track (Class C road)',
    speed_kmh:   30,
    notes:       'Compacted gravel or reinforced dirt track. Typical forward area supply route.',
  },
  UNPAVED_UNIMPROVED: {
    label:       'Unpaved Unimproved Track (Class D road)',
    speed_kmh:   15,
    notes:       'Dirt track / field. Highly weather-dependent. Cross-country logistics vehicle required.',
  },
};

/**
 * Terrain modifier applied to road speed.
 * Flat terrain = 1.0 baseline.
 */
export const MSR_TERRAIN_MODIFIERS = {
  FLAT: {
    label:    'Flat (plains / lowland)',
    modifier: 1.00,
    notes:    'Danube plain, Polish lowland. No gradient penalty.',
  },
  ROLLING: {
    label:    'Rolling (hills)',
    modifier: 0.85,
    notes:    'Carpathian foothills, Transylvanian basin. Moderate gradient.',
  },
  MOUNTAINOUS: {
    label:    'Mountainous',
    modifier: 0.65,
    notes:    'Carpathian / Alpine passes. Steep grades, hairpin bends. Significant speed penalty.',
  },
  MIXED: {
    label:    'Mixed terrain',
    modifier: 1.10,
    notes:    'Combination of flat and rolling/hilly sectors. Slight advantage where routes exploit flat corridors.',
  },
};

/**
 * Season / surface condition modifier applied on top of terrain modifier.
 * Captures weather-induced degradation (Ukraine 2022-2024: rasputitsa reduces
 * unpaved road speed by 75% in spring thaw / autumn rain).
 */
export const MSR_SEASON_MODIFIERS = {
  DRY:  {
    label:    'Dry (summer / winter frozen)',
    modifier: 1.00,
    notes:    'Best condition. Road surfaces firm.',
  },
  WET:  {
    label:    'Wet (rain / autumn)',
    modifier: 0.80,
    notes:    'Paved roads marginally affected; unpaved routes significantly degraded.',
  },
  MUD:  {
    label:    'Mud / Rasputitsa (spring thaw / heavy rain)',
    modifier: 0.55,
    notes:    'Ukraine data: rasputitsa reduces unpaved road speed by ~75%. Even paved routes congested by breakdown/diversion.',
  },
  SNOW: {
    label:    'Snow / Ice',
    modifier: 0.70,
    notes:    'Black ice and packed snow. Chains mandatory. Reduced loads for vehicle traction.',
  },
};

/**
 * Default MSR configuration (used when Tab4 has not saved an MSR config yet).
 * Central Europe theater, deliberate operations baseline.
 */
export const MSR_DEFAULTS = {
  distance_km:       50,
  road_type:         'PAVED_SECONDARY',
  terrain:           'ROLLING',
  season:            'DRY',
};

// ============================================================================
// HELPER FUNCTIONS
// Calculator integration
// ============================================================================

/**
 * Calculate Medical Days of Supply
 * @param {Object} currentStock - Current inventory levels
 * @param {number} dailyCasualties - Expected daily casualty rate
 * @param {Object} casualtyMix - Distribution by triage category
 * @returns {Object} MDOS analysis
 */
export const calculateMDOS = (currentStock, dailyCasualties, casualtyMix = {}) => {
  // Use DEFAULT_CASUALTY_MIX for any missing values
  const mix = {
    t1: casualtyMix.t1 ?? DEFAULT_CASUALTY_MIX.t1,
    t2: casualtyMix.t2 ?? DEFAULT_CASUALTY_MIX.t2,
    t3: casualtyMix.t3 ?? DEFAULT_CASUALTY_MIX.t3,
    dnbi: casualtyMix.dnbi ?? DEFAULT_CASUALTY_MIX.dnbi
  };
  
  // Calculate daily consumption by category
  const dailyConsumption = calculateDailyConsumption(dailyCasualties, mix);
  
  // Calculate DOS for each critical category
  const mdosByCategory = {};
  
  // IV Fluids
  if (currentStock.iv_fluids_liters !== undefined && dailyConsumption.iv_fluids_liters > 0) {
    mdosByCategory.iv_fluids = {
      current: currentStock.iv_fluids_liters,
      daily_consumption: dailyConsumption.iv_fluids_liters,
      dos: Math.floor(currentStock.iv_fluids_liters / dailyConsumption.iv_fluids_liters)
    };
  }
  
  // Blood products
  if (currentStock.prbc_units !== undefined && dailyConsumption.prbc_units > 0) {
    mdosByCategory.prbc = {
      current: currentStock.prbc_units,
      daily_consumption: dailyConsumption.prbc_units,
      dos: Math.floor(currentStock.prbc_units / dailyConsumption.prbc_units)
    };
  }
  
  if (currentStock.ffp_units !== undefined && dailyConsumption.ffp_units > 0) {
    mdosByCategory.ffp = {
      current: currentStock.ffp_units,
      daily_consumption: dailyConsumption.ffp_units,
      dos: Math.floor(currentStock.ffp_units / dailyConsumption.ffp_units)
    };
  }
  
  // Hemorrhage control
  if (currentStock.tourniquets !== undefined && dailyConsumption.tourniquets > 0) {
    mdosByCategory.tourniquets = {
      current: currentStock.tourniquets,
      daily_consumption: dailyConsumption.tourniquets,
      dos: Math.floor(currentStock.tourniquets / dailyConsumption.tourniquets)
    };
  }
  
  if (currentStock.hemostatic_dressings !== undefined && dailyConsumption.hemostatic_dressings > 0) {
    mdosByCategory.hemostatic = {
      current: currentStock.hemostatic_dressings,
      daily_consumption: dailyConsumption.hemostatic_dressings,
      dos: Math.floor(currentStock.hemostatic_dressings / dailyConsumption.hemostatic_dressings)
    };
  }
  
  // Controlled substances
  if (currentStock.morphine_doses !== undefined && dailyConsumption.morphine_doses > 0) {
    mdosByCategory.controlled = {
      current: currentStock.morphine_doses,
      daily_consumption: dailyConsumption.morphine_doses,
      dos: Math.floor(currentStock.morphine_doses / dailyConsumption.morphine_doses)
    };
  }
  
  // TXA
  if (currentStock.txa_grams !== undefined && dailyConsumption.txa_grams > 0) {
    mdosByCategory.txa = {
      current: currentStock.txa_grams,
      daily_consumption: dailyConsumption.txa_grams,
      dos: Math.floor(currentStock.txa_grams / dailyConsumption.txa_grams)
    };
  }
  
  // Calculate overall MDOS (minimum of all categories)
  // FIX T4-002: Return 0 (not 999) when stock is empty/zero
  const dosValues = Object.values(mdosByCategory)
    .map(cat => cat.dos)
    .filter(dos => dos !== undefined && dos !== Infinity && !isNaN(dos));
  
  // T4-002 FIX: When no valid DOS values exist (all stock = 0), return 0 not 999
  const overallMDOS = dosValues.length > 0 ? Math.min(...dosValues) : 0;
  
  // Determine status
  // T4-002 FIX: Add explicit handling for MDOS = 0
  let status = 'OPTIMAL';
  let color = 'success';
  let action = 'Maintain current levels';
  
  if (overallMDOS === 0) {
    status = 'CRITICAL_ZERO';
    color = 'error';
    action = 'NO SUPPLIES - Immediate emergency resupply required';
  } else if (overallMDOS <= MDOS_STANDARDS.STATUS.EMERGENCY.days) {
    status = 'EMERGENCY';
    color = 'error';
    action = MDOS_STANDARDS.STATUS.EMERGENCY.action;
  } else if (overallMDOS <= MDOS_STANDARDS.STATUS.CRITICAL.days) {
    status = 'CRITICAL';
    color = 'error';
    action = MDOS_STANDARDS.STATUS.CRITICAL.action;
  } else if (overallMDOS <= MDOS_STANDARDS.STATUS.LOW.days) {
    status = 'LOW';
    color = 'warning';
    action = MDOS_STANDARDS.STATUS.LOW.action;
  } else if (overallMDOS <= MDOS_STANDARDS.STATUS.ADEQUATE.days) {
    status = 'ADEQUATE';
    color = 'info';
    action = MDOS_STANDARDS.STATUS.ADEQUATE.action;
  }
  
  // Find limiting factors
  const limitingFactors = Object.entries(mdosByCategory)
    .filter(([_, data]) => data.dos <= 7)
    .sort((a, b) => a[1].dos - b[1].dos)
    .map(([name, data]) => ({
      category: name,
      dos: data.dos,
      severity: data.dos <= 3 ? 'CRITICAL' : data.dos <= 5 ? 'LOW' : 'WARNING'
    }));
  
  return {
    overall_mdos: overallMDOS,
    status,
    color,
    action,
    
    by_category: mdosByCategory,
    daily_consumption: dailyConsumption,
    
    limiting_factors: limitingFactors,
    limiting_factor: limitingFactors[0]?.category || 'none',
    
    recommendations: generateMDOSRecommendations(overallMDOS, mdosByCategory, limitingFactors)
  };
};

/**
 * Calculate daily consumption based on casualties
 */
export const calculateDailyConsumption = (dailyCasualties, mix) => {
  const t1 = CONSUMPTION_RATES.T1_IMMEDIATE;
  const t2 = CONSUMPTION_RATES.T2_DELAYED;
  const t3 = CONSUMPTION_RATES.T3_MINIMAL;
  const dnbi = CONSUMPTION_RATES.DNBI;
  
  const t1Count = dailyCasualties * mix.t1;
  const t2Count = dailyCasualties * mix.t2;
  const t3Count = dailyCasualties * mix.t3;
  const dnbiCount = dailyCasualties * mix.dnbi;
  
  // Apply Ukraine lessons - multiply by variance factors
  const ukraineMultiplier = {
    tourniquets: 1.5,      // +50% based on Ukraine data
    hemostatic: 1.5,
    txa: 1.5,
    blood: 1.3
  };
  
  return {
    // IV Fluids
    iv_fluids_liters: Math.round(
      (t1Count * t1.pharmaceuticals.iv_fluids_liters) +
      (t2Count * t2.pharmaceuticals.iv_fluids_liters) +
      (t3Count * t3.pharmaceuticals.iv_fluids_liters) +
      (dnbiCount * dnbi.pharmaceuticals.iv_fluids_liters)
    ),
    
    // Blood products
    prbc_units: Math.round(
      ((t1Count * t1.blood_products.prbc_units) +
       (t2Count * t2.blood_products.prbc_units)) * ukraineMultiplier.blood
    ),
    
    ffp_units: Math.round(
      ((t1Count * t1.blood_products.ffp_units) +
       (t2Count * t2.blood_products.ffp_units)) * ukraineMultiplier.blood
    ),
    
    // TXA
    txa_grams: Math.round(
      ((t1Count * t1.pharmaceuticals.txa_grams) +
       (t2Count * t2.pharmaceuticals.txa_grams)) * ukraineMultiplier.txa
    ),
    
    // Controlled substances
    morphine_doses: Math.round(
      (t1Count * t1.pharmaceuticals.morphine_doses) +
      (t2Count * t2.pharmaceuticals.morphine_doses)
    ),
    
    ketamine_mg: Math.round(
      (t1Count * t1.pharmaceuticals.ketamine_mg) +
      (t2Count * t2.pharmaceuticals.ketamine_mg)
    ),
    
    // Hemorrhage control
    tourniquets: Math.round(
      ((t1Count * t1.consumables.tourniquets) +
       (t2Count * t2.consumables.tourniquets)) * ukraineMultiplier.tourniquets
    ),
    
    hemostatic_dressings: Math.round(
      ((t1Count * t1.consumables.hemostatic_dressings) +
       (t2Count * t2.consumables.hemostatic_dressings)) * ukraineMultiplier.hemostatic
    ),
    
    chest_seals: Math.round(
      (t1Count * t1.consumables.chest_seals) +
      (t2Count * t2.consumables.chest_seals)
    ),
    
    // Dressings
    dressing_sets: Math.round(
      (t1Count * t1.consumables.dressing_sets) +
      (t2Count * t2.consumables.dressing_sets) +
      (t3Count * t3.consumables.dressing_sets)
    ),
    
    // Hypothermia
    hypothermia_blankets: Math.round(
      (t1Count * t1.consumables.hypothermia_blankets) +
      (t2Count * t2.consumables.hypothermia_blankets) +
      (t3Count * t3.consumables.hypothermia_blankets)
    ),
    
    // Weight estimate
    total_weight_kg: Math.round(
      (t1Count * t1.weight_kg_total) +
      (t2Count * t2.weight_kg_total) +
      (t3Count * t3.weight_kg_total) +
      (dnbiCount * dnbi.weight_kg_total)
    )
  };
};

/**
 * Generate MDOS recommendations
 */
const generateMDOSRecommendations = (mdos, byCategory, limitingFactors) => {
  const recommendations = [];
  
  if (mdos <= 3) {
    recommendations.push({
      priority: 'CRITICAL',
      type: 'RESUPPLY',
      message: 'EMERGENCY resupply required within 24 hours',
      actions: [
        'Request emergency medical materiel resupply',
        'Helicopter resupply if ground blocked',
        'Ration critical items',
        'Report to higher headquarters'
      ]
    });
  } else if (mdos <= 5) {
    recommendations.push({
      priority: 'HIGH',
      type: 'RESUPPLY',
      message: 'Urgent resupply required within 72 hours',
      actions: [
        'Submit priority resupply request',
        'Identify alternative supply sources',
        'Review consumption rates'
      ]
    });
  } else if (mdos <= 10) {
    recommendations.push({
      priority: 'MODERATE',
      type: 'RESUPPLY',
      message: 'Routine resupply needed within 7 days',
      actions: ['Schedule routine resupply', 'Monitor consumption trends']
    });
  }
  
  // Category-specific recommendations
  limitingFactors.forEach(factor => {
    if (factor.severity === 'CRITICAL') {
      recommendations.push({
        priority: 'CRITICAL',
        type: 'SHORTAGE',
        message: `${factor.category.toUpperCase()} at critical level (${factor.dos} DOS)`,
        actions: [`Request emergency ${factor.category} resupply`, 'Consider rationing protocol']
      });
    }
  });
  
  // Blood product specific
  if (byCategory.prbc?.dos <= 3) {
    recommendations.push({
      priority: 'CRITICAL',
      type: 'BLOOD',
      message: 'Blood product shortage imminent',
      actions: [
        'Activate Walking Blood Bank',
        'Request emergency blood shipment',
        'Consider transfer to facility with blood'
      ]
    });
  }
  
  return recommendations;
};

/**
 * Calculate resupply requirements
 */
export const getResupplyRequirements = (currentStock, targetDOS, dailyCasualties, casualtyMix = {}) => {
  // Use DEFAULT_CASUALTY_MIX for any missing values
  const mix = {
    t1: casualtyMix.t1 ?? DEFAULT_CASUALTY_MIX.t1,
    t2: casualtyMix.t2 ?? DEFAULT_CASUALTY_MIX.t2,
    t3: casualtyMix.t3 ?? DEFAULT_CASUALTY_MIX.t3,
    dnbi: casualtyMix.dnbi ?? DEFAULT_CASUALTY_MIX.dnbi
  };
  
  const dailyConsumption = calculateDailyConsumption(dailyCasualties, mix);
  const requirements = {};
  
  // Calculate target stock and shortfall for each item
  Object.entries(dailyConsumption).forEach(([item, dailyAmount]) => {
    if (dailyAmount > 0 && item !== 'total_weight_kg') {
      const targetStock = dailyAmount * targetDOS;
      const current = currentStock[item] || 0;
      const shortfall = Math.max(0, targetStock - current);
      
      requirements[item] = {
        current,
        target: Math.round(targetStock),
        shortfall: Math.round(shortfall),
        daily_consumption: Math.round(dailyAmount),
        priority: shortfall > targetStock * 0.5 ? 'HIGH' : shortfall > 0 ? 'NORMAL' : 'NONE'
      };
    }
  });
  
  // Calculate logistics requirements
  const totalShortfall = Object.values(requirements)
    .reduce((sum, item) => sum + item.shortfall, 0);
  
  // Estimate weight (rough estimate)
  const estimatedWeight = totalShortfall * 0.5;  // Average 0.5 kg per item
  
  return {
    requirements,
    
    logistics: {
      estimated_weight_kg: Math.round(estimatedWeight),
      lmtv_loads: Math.ceil(estimatedWeight / 2500),
      hemtt_loads: Math.ceil(estimatedWeight / 10000)
    },
    
    priority: Object.values(requirements).some(r => r.priority === 'HIGH') ? 'URGENT' : 'ROUTINE',
    
    suggested_package: estimatedWeight > 500 ? 'MASS_CASUALTY_PUSH' :
                       estimatedWeight > 100 ? 'SURGICAL_RESUPPLY' :
                       'ROLE_1_DAILY'
  };
};

/**
 * Get consumption rate for specific casualty type
 */
export const getConsumptionRate = (casualtyType) => {
  const typeMap = {
    'T1': 'T1_IMMEDIATE',
    'T2': 'T2_DELAYED',
    'T3': 'T3_MINIMAL',
    'T4': 'T4_EXPECTANT',
    'DNBI': 'DNBI'
  };
  
  return CONSUMPTION_RATES[typeMap[casualtyType]] || CONSUMPTION_RATES.T3_MINIMAL;
};

/**
 * Calculate supply weight for evacuation planning
 */
export const calculateSupplyWeight = (casualties, casualtyMix = {}) => {
  // Use DEFAULT_CASUALTY_MIX for any missing values
  const t1Count = casualties * (casualtyMix.t1 ?? DEFAULT_CASUALTY_MIX.t1);
  const t2Count = casualties * (casualtyMix.t2 ?? DEFAULT_CASUALTY_MIX.t2);
  const t3Count = casualties * (casualtyMix.t3 ?? DEFAULT_CASUALTY_MIX.t3);
  const dnbiCount = casualties * (casualtyMix.dnbi ?? DEFAULT_CASUALTY_MIX.dnbi);
  
  const weight = 
    (t1Count * CONSUMPTION_RATES.T1_IMMEDIATE.weight_kg_total) +
    (t2Count * CONSUMPTION_RATES.T2_DELAYED.weight_kg_total) +
    (t3Count * CONSUMPTION_RATES.T3_MINIMAL.weight_kg_total) +
    (dnbiCount * CONSUMPTION_RATES.DNBI.weight_kg_total);
  
  return {
    total_weight_kg: Math.round(weight),
    per_casualty_avg_kg: Math.round(weight / casualties),
    lmtv_loads: Math.ceil(weight / 2500)
  };
};

/**
 * Validate stock levels against MDOS standards
 */
export const validateStockLevels = (stock, echelon = 'ROLE_2_BASIC') => {
  const standards = MDOS_STANDARDS.BY_ECHELON[echelon] || MDOS_STANDARDS.BY_ECHELON.ROLE_2_BASIC;
  const errors = [];
  const warnings = [];
  
  // Check critical items are defined
  const criticalItems = ['prbc_units', 'ffp_units', 'iv_fluids_liters', 'tourniquets', 'morphine_doses'];
  
  criticalItems.forEach(item => {
    if (stock[item] === undefined) {
      warnings.push(`${item} stock level not defined`);
    } else if (stock[item] === 0) {
      errors.push(`${item} is at ZERO - critical shortage`);
    }
  });
  
  // Validate against MDOS standards
  const mdosValidation = {
    echelon_label: standards.label,
    standard_dos: standards.standard_dos,
    minimum_dos: standards.minimum_dos,
    maximum_dos: standards.maximum_dos,
    resupply_frequency: standards.resupply_frequency,
    notes: standards.notes
  };
  
  // Add warning if stock levels suggest below minimum DOS
  if (stock.current_dos !== undefined && stock.current_dos < standards.minimum_dos) {
    warnings.push(`Current DOS (${stock.current_dos}) is below minimum (${standards.minimum_dos}) for ${standards.label}`);
  }
  
  return { 
    errors, 
    warnings, 
    isValid: errors.length === 0,
    severity: errors.length > 0 ? 'ERROR' : warnings.length > 0 ? 'WARNING' : 'OK',
    standards_applied: mdosValidation
  };
};

// ============================================================================
// DEFAULT STOCK LEVELS
// ============================================================================

export const DEFAULT_ROLE_1_STOCK = {
  iv_fluids_liters: 50,
  morphine_doses: 20,
  ketamine_vials: 10,
  txa_vials: 20,
  antibiotics_doses: 30,
  
  tourniquets: 30,
  hemostatic_dressings: 20,
  chest_seals: 10,
  dressing_packs: 30,
  hypothermia_blankets: 10,
  
  oxygen_d_cylinders: 4
};

export const DEFAULT_ROLE_2_STOCK = {
  // Pharmaceuticals
  iv_fluids_liters: 500,
  morphine_doses: 200,
  ketamine_vials: 50,
  txa_grams: 100,
  antibiotics_doses: 300,
  anesthetics_kits: 50,
  emergency_meds_doses: 150,
  
  // Blood products
  prbc_units: 100,
  ffp_units: 100,
  platelets_units: 20,
  cryo_units: 30,
  
  // Consumables
  tourniquets: 100,
  hemostatic_dressings: 100,
  chest_seals: 50,
  dressing_sets: 200,
  iv_start_kits: 300,
  hypothermia_blankets: 50,
  
  // REMOVED FROM APP — surgical_kits not a NATO planning item. Retained for reference only.
  // surgical_kits: 30,
  suture_packs: 200,
  
  // Equipment
  ventilators: 4,
  monitors: 8,
  defibrillators: 2,
  
  // Medical gases
  oxygen_e_cylinders: 20
};

// ============================================================================
// EDUCATIONAL CONTENT
// ============================================================================

export const CLASS_VIII_EDUCATIONAL = {
  key_principles: [
    'Medical materiel is life-saving - prioritize resupply',
    'MDOS below 5 is CRITICAL - immediate action required',
    'Ukraine showed 2-3x consumption vs pre-war planning',
    'Cold chain for blood products is non-negotiable',
    'Forward position hemorrhage control supplies heavily'
  ],
  
  ukraine_lessons: [
    'Tourniquet consumption 3x planning estimates',
    'TXA now standard for ALL trauma - stock accordingly',
    'Hemostatic dressings were critical shortage',
    'Blood product usage 2x NATO factors',
    'Hypothermia prevention items essential'
  ],
  
  common_errors: [
    'Underestimating hemorrhage control consumption',
    'Not accounting for Ukraine consumption multipliers',
    'Assuming static consumption rates (varies by intensity)',
    'Forgetting surgical resupply for Role 2E',
    'Neglecting cold chain for forward blood'
  ]
};

// ============================================================================
// EXPORT ALL
// ============================================================================

const ClassVIII = {
  CLASS_VIII_CATEGORIES,
  CONSUMPTION_RATES,
  DEFAULT_CASUALTY_MIX,
  BLOOD_PRODUCT_ECHELON_POLICY,
  MDOS_STANDARDS,
  DOS_THRESHOLDS,
  REQUEST_LEAD_TIMES,
  WASTAGE_FACTORS,
  BED_NEED_FACTORS,
  MTF_BASE_CONSUMPTION_RATES,
  PUSH_PACKAGES,
  getPackagesForDestination,
  RESUPPLY_FACTORS,
  UKRAINE_LOGISTICS_LESSONS,
  calculateMDOS,
  calculateDailyConsumption,
  getResupplyRequirements,
  getConsumptionRate,
  calculateSupplyWeight,
  validateStockLevels,
  DEFAULT_ROLE_1_STOCK,
  DEFAULT_ROLE_2_STOCK,
  CLASS_VIII_EDUCATIONAL
};

// ============================================================================
// ENH-008: DRONE UAS MEDICAL SUPPLY DELIVERY SPECIFICATIONS
//
// Source: Ukraine 2022-2024 operational practice + US Army Project Convergence 22
//         (Fort Irwin, Oct 2022) + 173rd Airborne Brigade exercise Lithuania 2024.
//         No promulgated NATO STANAG as of March 2026 — emerging operational
//         capability. Framed as Ukraine/exercise data, same category as drone
//         casualty rate data already in the tool.
//
// Key Ukraine lesson: cargo drones deliver blood and tourniquets forward of R2B
// in contested airspace where ground convoys and MEDEVAC helicopters cannot operate.
// FPV-class drones carry 2–5 kg; dedicated cargo drones carry 10–20 kg.
//
// AD threat degrades drone availability on the same axis as MEDEVAC helicopters,
// but drones operate at very low altitude (NOE or ground-hugging) so the modifier
// is less severe than for manned aircraft.
//
// Cold chain note: blood products are viable after drone delivery if flight time
// < 2h and ambient temperature < 25°C (PMC study, Lammers 2023, Transfusion).
// The APRU-6L autonomous portable refrigeration unit maintains 1–6°C.
// Cold chain viability is flagged in the event record but not zeroed — planner
// must verify ambient temperature and flight time in the scenario.
// ============================================================================

export const DRONE_DELIVERY_SPECS = {
  // Small cargo drone — primary blood/tourniquet forward resupply platform
  // Ukraine practice: repurposed commercial cargo drone (e.g. DJI Agras M30, Mugin-5)
  CARGO_DRONE: {
    label:                  'Cargo Drone (Medical)',
    description:            'Autonomous cargo drone for forward blood product and TCCC supply delivery.',
    payload_kg:             10,             // usable medical payload (kg)
    blood_units_per_flight: 12,             // PRBC units per flight (@ 0.40 kg/unit + packaging)
    tourniquet_packs:       80,             // CAT tourniquet packs per flight (@ 0.09 kg each)
    range_km:               30,             // one-way operational range
    flight_time_minutes:    45,             // typical one-way flight to 15 km forward position
    cold_chain_viable:      true,           // viable if < 2h flight, < 25°C ambient (Lammers 2023)
    cold_chain_note:        'Blood viable if flight time < 2h and ambient < 25°C. Verify in scenario.',
    // AD threat availability modifier (less severe than manned MEDEVAC — NOE flight profile)
    ad_threat_availability: {
      PERMISSIVE: 1.00,
      LOW:        0.90,
      MEDIUM:     0.75,
      HIGH:       0.50,
      DENIED:     0.20,   // extreme risk but not zero — small radar cross-section, low altitude
    },
    source: 'Ukraine 2022-2024 / US Army Project Convergence 22 / Lammers 2023 Transfusion',
  },

  // Light drop drone — tourniquets and TCCC consumables only, not blood
  LIGHT_DROP_DRONE: {
    label:                  'Light Drop Drone (TCCC Supplies)',
    description:            'FPV-class or small cargo drone for TCCC consumables. Not cold-chain capable.',
    payload_kg:             3,
    blood_units_per_flight: 0,              // not cold-chain capable — no blood products
    tourniquet_packs:       25,
    range_km:               10,
    flight_time_minutes:    15,
    cold_chain_viable:      false,
    cold_chain_note:        'Not suitable for blood products. TCCC consumables only.',
    ad_threat_availability: {
      PERMISSIVE: 1.00,
      LOW:        0.92,
      MEDIUM:     0.80,
      HIGH:       0.60,
      DENIED:     0.30,   // very small target, low altitude — higher survivability than cargo drone
    },
    source: 'Ukraine 2022-2024 operational data (FPV medical drops observed at contact line)',
  },
};

// Default drone type for Tab4 resupply events
export const DRONE_DEFAULT_TYPE = 'CARGO_DRONE';

// Drone delivery event identifier — used in resupply event routing in supplyChainModule
export const DRONE_DELIVERY_METHOD = 'DRONE';

export default ClassVIII;
